import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Variables, VariableType } from '~/core/classes/user/variables';
import { AppConfigService } from '~/core/services/appconfig.service';
import { GraphService } from '~/core/services/graph.service';
import { formatMDY } from '~/core/utils/date-format';

import { HomeApiService } from './home-api.service';

interface HomeMetricCard {
    title: string;
    value: string;
    filterKey?: SummaryMetricFilterKey;
    tooltip?: string;
}

type SummaryMetricFilterKey = 'allRows' | 'potentialReliabilityRisk' | 'safetyRiskType' | 'potentialLpoRiskType';

interface RiskRegisterColumn {
    key: keyof RiskRegisterRow;
    label: string;
    dividerAfter?: boolean;
    sortable?: boolean;
}

type RiskRegisterSortKey = 'score' | 'aiAdjustedRiskScore';
type RiskRegisterSortDirection = 'asc' | 'desc';

interface MonthlyReportFailureAnalysis {
    issue_type: string;
    asset: string;
    description: string;
    detail: string;
}

interface MonthlyReportKeyFinding {
    category: string;
    finding: string;
    reliability_impact: string;
}

interface MonthlyReportOpportunity {
    opportunity: string;
    expected_benefit: string;
}

interface MonthlyReportItemRequiringAttention {
    priority: string;
    asset_system: string;
    risk_description: string;
    potential_impact: string;
    recommended_action: string;
}

interface MonthlyReportItem {
    file: string;
    date: string;
    summary: string;
    key_findings: MonthlyReportKeyFinding[];
    asset_mentions?: MonthlyReportAssetMention[];
    operational_concerns?: string[];
    items_requiring_attention?: MonthlyReportItemRequiringAttention[];
    opportunities?: MonthlyReportOpportunity[];
    failure_analysis: MonthlyReportFailureAnalysis[];
    executive_outlook?: { primary_risk: string; primary_opportunity: string };
}

interface MonthlyReportAssetMention {
    tag: string;
    description: string;
    context: string;
    significance: string;
    resolved: boolean;
}

interface MonthlyReportResponse {
    run_id: string;
    reports: MonthlyReportItem[];
}

interface MonthlyReportIssueRow {
    type: string;
    assetUnit: string;
    issueDescription: string;
    detailRootCause: string;
}

interface MonthlyEquipmentReferencedRow {
    tag: string;
    equipmentName: string;
    contextFromReport: string;
    significance: string;
}

interface MonthlyIssueSummaryChip {
    label: string;
    count: number;
    tone: 'failure' | 'trip' | 'delay' | 'linked' | 'other';
}

interface RunOptionResponse {
    run_id: string;
    run_id_month_year?: string;
}

interface RunOption {
    runId: string;
    label: string;
}

interface RefineryLandingRunResponse {
    run_id: string;
    run_id_month_year?: string;
}

interface RefineryLandingUnitResponse {
    unit: string;
    runs: RefineryLandingRunResponse[];
}

interface RefineryLandingResponse {
    refinery: string;
    units: RefineryLandingUnitResponse[];
}

interface UnitLandingCard {
    id: string;
    label: string;
    imageUrl: string;
    runs: RunOption[];
}

interface RefineryLandingCard {
    id: string;
    label: string;
    imageUrl: string;
    units: UnitLandingCard[];
}

type HomeLandingStage = 'refinery' | 'unit' | 'dashboard';

interface RunSummaryResponse {
    run_id: string;
    refinery: string;
    unit: string;
    date_from: string;
    date_to: string;
    assets_analysed?: number;
    assets_analyzed?: number;
    bad_actor_candidates: number;
    safety_risk_type_count: number;
    lpo_risk_type_count: number;
    regeneration_required?: boolean;
    generated_at?: string;
}

interface ApprovalActionRequest {
    action_type: 'confirm' | 'modify';
    comment?: string;
}

interface RiskTypeApprovalRequest {
    action: ApprovalActionRequest;
    risk_type: string;
}

interface FailureTypeApprovalRequest {
    action: ApprovalActionRequest;
    failure_mode_code: string;
    failure_mode_description: string;
}

interface MainConcernApprovalRequest {
    action: ApprovalActionRequest;
    main_concern: string;
}

interface RecommendedActionApprovalRequest {
    action: ApprovalActionRequest;
    recommended_action: string;
}

interface BulkAssetApprovalRequest {
    remarks: string;
    risk_type: RiskTypeApprovalRequest;
    failure_type: FailureTypeApprovalRequest;
    main_concern: MainConcernApprovalRequest;
    recommended_action: RecommendedActionApprovalRequest;
}

interface BulkAssetApprovalResponse {
    success: boolean;
    message?: string;
}

interface DeleteAssetResponse {
    success?: boolean;
    is_deleted?: boolean;
    message?: string;
}

interface RunAssetsApiRow {
    risk_rank: number;
    asset_tag: string;
    location_description: string;
    wo_status_label: string;
    priority_number: number | null;
    priority_label: string;
    risk_type: string;
    criticality: string;
    failure_type: string;
    rsl_count: number;
    stl_count: number;
    iow_count: number;
    ord_count: number;
    predix_count: number;
    maximo_count: number;
    process_count: number;
    rims_count: number;
    score_100: number;
    band: string;
    is_bad_actor: boolean;
    severity: number;
    trend_worsening: number;
    explainability_score: number | null;
    semantic_risk_score?: number | null;
    semantic_supporting_evidence?: unknown[];
    rationale?: string | null;
    semantic_risk_reason?: string | null;
    ai_adjusted_risk_score?: number | null;
    ai_adjusted_risk_calculation?: string | null;
    risk_trend: string | null;
    citation_count: number;
    score_factors: Record<string, number>;
    is_deleted?: boolean;
    deleted_at?: string | null;
    deleted_by?: string | null;
    deleted_by_name?: string | null;
    deleted_comment?: string | null;
}

interface RunAssetsResponse {
    run_id: string;
    total_items: number;
    items: RunAssetsApiRow[];
}

interface AssetDetailExplainabilityFactorApi {
    key: string;
    label: string;
    weight: number;
    value: number;
    contribution_pts: number;
    formula: string;
}

interface AssetDetailExplainabilityApi {
    score: number;
    factors: AssetDetailExplainabilityFactorApi[];
}

interface AssetDetailResponse extends RunAssetsApiRow {
    narrative?: string;
    main_concern?: string;
    main_concern_reasoning?: string;
    likely_failure_mode?: string;
    recommended_action?: string;
    recommended_action_reasoning?: string;
    chronological_walkthrough?: string;
    chronology_pattern?: string;
    trend_summary?: string;
    cm_wos?: number;
    cm_cost_total?: number;
    explainability?: AssetDetailExplainabilityApi;
    pm_wo_mentions?: Array<string | Record<string, unknown>>;
    citation_ids?: string[];
    approval_dropdowns?: AssetDetailApprovalDropdowns;
    approval_status?: AssetDetailApprovalStatus;
}

interface AssetDetailApprovalDropdowns {
    risk_type?: string[];
    failure_type?: string[];
}

interface AssetDetailApprovalHistoryEntry {
    value?: unknown;
    action_type?: string;
    modified_by?: string | null;
    modified_at?: string | null;
    comment?: string;
}

interface AssetDetailApprovalFieldStatus {
    field_name?: string;
    original_value?: unknown;
    current_value?: unknown;
    is_modified?: boolean;
    modified_by?: string | null;
    modified_at?: string | null;
    comment?: string;
    modification_history?: AssetDetailApprovalHistoryEntry[];
}

interface AssetDetailApprovalStatus {
    is_modified?: boolean;
    modified_by?: string | null;
    modified_at?: string | null;
    remarks?: string;
    risk_type?: AssetDetailApprovalFieldStatus;
    failure_type?: AssetDetailApprovalFieldStatus;
    main_concern?: AssetDetailApprovalFieldStatus;
    recommended_action?: AssetDetailApprovalFieldStatus;
}

interface ViewActionHistoryEntry {
    value: string;
    actionType: string;
    modifiedBy: string;
    rawModifiedBy: string;
    modifiedAt: string;
    modifiedAtEpoch: number;
    comment: string;
    modifiedByGroupIds: string[];
}

interface GroupMemberIdentity {
    id?: string;
    userPrincipalName?: string;
    mail?: string;
    displayName?: string;
}

interface ViewActionFieldHistory {
    fieldLabel: string;
    originalValue: string;
    currentValue: string;
    modifiedAtEpoch: number;
    entries: ViewActionHistoryEntry[];
}

type PreviousActionFilterKey = 'engineRe' | 'mfgRe';

type FilterFieldKey = 'criticality' | 'potentialBadActor' | 'risk_trend' | 'woStatus' | 'riskType' | 'dataSource';

interface RiskRegisterRow {
    rank: string;
    assetTag: string;
    assetDescription: string;
    woStatus: string;
    priority: string;
    riskType: string;
    criticality: string;
    failureType: string;
    rsl: string;
    stl: string;
    iow: string;
    ord: string;
    predix: string;
    maximoCmWos: string;
    score: string;
    scoreFactors?: Array<{ key: string; value: string }>;
    potentialBadActor: string;
    severity: string;
    band: string;
    aiAdjustedRiskScore: string;
    aiAdjustedRiskCalculation?: string[];
    semanticRiskScore: string;
    semanticSupportingEvidence: string[];
    semanticRationale: string;
    explTrend: string;
    risk_trend: string;
    isDeleted: boolean;
    deletedAt?: string | null;
    deletedByName?: string | null;
    deletedComment?: string | null;
    isExpanded: boolean;
}

type RiskDetailSectionKey =
    | 'chronologicalWalkthrough'
    | 'trendAnalysis'
    | 'preventiveMaintenance'
    | 'explainability'
    | 'aiAdjustedRiskScore'
    | 'sourceMentions';

interface RiskDetailFactor {
    label: string;
    value: string;
    points: string;
    formula: string;
}

interface RiskDetailTopSummary {
    assetTag: string;
    riskType: string;
    criticality: string;
    score: string;
}

interface RiskEditForm {
    mainConcern: string;
    recommendedAction: string;
    comment: string;
    riskType: string;
    failureType: string;
}

interface RiskEditDropdownState {
    open: boolean;
    search: string;
}

interface RiskDetailSourceMention {
    source: string;
    date: string;
    description: string;
    repetitionTimes: string;
    woStatus: string;
    ref: string;
}

interface RiskDetailTrendPoint {
    date: string;
    content: string;
}

interface AssetSourceMentionApiRow {
    source: string;
    date_occurred?: string;
    description?: string;
    repetition_times?: number;
    wo_status?: string;
    record_ref?: string;
}

interface AssetSourceMentionsResponse {
    items: AssetSourceMentionApiRow[];
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
}

interface CitationsManifestDetail {
    id?: string;
    citation_id?: string;
    [key: string]: unknown;
}

interface CitationsManifestResponse {
    generated_at?: string;
    total_citations?: number;
    by_asset?: Record<string, string[]>;
    citations?: Record<string, CitationsManifestDetail>;
}

interface PdfRegenerationStatusResponse {
    is_stale?: boolean;
    regeneration_required?: boolean;
    regeneration_in_progress?: boolean;
    regeneration_stuck?: boolean;
    last_regenerated_at?: string | null;
    regeneration_started_at?: string | null;
    has_approvals?: boolean;
    latest_modification_at?: string | null;
    pdf_base64?: string | null;
}

interface PdfRegenerationTriggerResponse {
    success?: boolean;
    regeneration_started?: boolean;
    message?: string;
    status?: PdfRegenerationStatusResponse;
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    public readonly reportPreparedFor = 'Chevron ENGINE Reliability Team, Richmond FCCU Reliability Team.';
    public reportTitle = '--';
    public readonly reportingPeriodLabel = 'Reporting period:';
    public reportingPeriodValue = '--';
    public runSummaryLastUpdatedLabel = '--';
    public runSummaryRegenerationRequired = false;

    public selectedRun = '';
    public runOptions: RunOption[] = [];
    public selectedRefineryId = '';
    public selectedUnitId = '';
    public currentLandingStage: HomeLandingStage = 'refinery';
    public refineryLandingCards: RefineryLandingCard[] = [];
    public isLandingCardsLoading = false;
    public hasLandingCardsLoadError = false;
    private pendingRouteSelection: { routeRefineryId: string; routeUnitId: string; routeRunId: string } | null = null;
    private isLandingCardsReady = false;
    public isRunOptionsLoading = false;
    public hasRunOptionsLoadError = false;
    public isRunSummaryLoading = false;
    public hasRunSummaryLoadError = false;
    public isRunAssetsLoading = false;
    public hasRunAssetsLoadError = false;
    public isMonthlyReportLoading = false;
    public hasMonthlyReportLoadError = false;
    public isPdfDownloading = false;
    public isPdfRegenerating = false;
    public isPdfRegenerationInProgress = false;
    public isDownloadConfirmationDialogOpen = false;
    public riskRegisterTotalResults = 0;
    private runSummaryRequestId = 0;
    private runAssetsRequestId = 0;
    private monthlyReportRequestId = 0;
    private pdfStatusPollTimer: ReturnType<typeof setInterval> | null = null;
    private readonly pdfStatusPollIntervalMs = 30000;

    public readonly riskRegisterTitle = 'Ranked Risk Register Table';
    public readonly processEngineeringMonthlyReportTitle = 'Monthly Leading Indicators Report Summary';
    public isRiskRegisterExpanded = true;
    public isProcessEngineeringMonthlyReportExpanded = true;
    public isFilterDialogOpen = false;
    public riskRegisterSearchQuery = '';
    public readonly riskRegisterPageSizeOptions: number[] = [10, 25, 50, 75, 100];
    public riskRegisterPageSize = this.riskRegisterPageSizeOptions[0];
    public currentRiskRegisterPage = 1;
    public riskRegisterSortKey: RiskRegisterSortKey | null = null;
    public riskRegisterSortDirection: RiskRegisterSortDirection | null = null;
    public activeFilterField: FilterFieldKey = 'criticality';
    public activeSummaryMetricFilter: SummaryMetricFilterKey | null = null;

    public readonly filterFields: Array<{ key: FilterFieldKey; label: string }> = [
        { key: 'criticality', label: 'Criticality' },
        { key: 'potentialBadActor', label: 'Potential Reliability Risk' },
        { key: 'risk_trend', label: 'Risk Trend (MoM)' },
        { key: 'woStatus', label: 'WO Status' },
        { key: 'riskType', label: 'Risk Type' },
        { key: 'dataSource', label: 'Data Sources' }
    ];

    public isAllCriticalitySelected = true;
    public isAllPotentialBadActorSelected = true;
    public isAllRiskTrendSelected = true;
    public isAllWoStatusSelected = true;
    public isAllRiskTypeSelected = true;
    public isAllDataSourceSelected = true;
    public selectedCriticalityValues: string[] = [];
    public selectedPotentialBadActorValues: string[] = [];
    public selectedRiskTrendValues: string[] = [];
    public selectedWoStatusValues: string[] = [];
    public selectedRiskTypeValues: string[] = [];
    public selectedDataSourceValues: string[] = [];

    public dialogIsAllCriticalitySelected = true;
    public dialogIsAllPotentialBadActorSelected = true;
    public dialogIsAllRiskTrendSelected = true;
    public dialogIsAllWoStatusSelected = true;
    public dialogIsAllRiskTypeSelected = true;
    public dialogIsAllDataSourceSelected = true;
    public dialogSelectedCriticalityValues: string[] = [];
    public dialogSelectedPotentialBadActorValues: string[] = [];
    public dialogSelectedRiskTrendValues: string[] = [];
    public dialogSelectedWoStatusValues: string[] = [];
    public dialogSelectedRiskTypeValues: string[] = [];
    public dialogSelectedDataSourceValues: string[] = [];

    public readonly dataSourceFilterOptions: Array<{ key: string; label: string }> = [
        { key: 'rsl', label: 'RSL' },
        { key: 'stl', label: 'STL' },
        { key: 'iow', label: 'IOW' },
        { key: 'ord', label: 'ORD' },
        { key: 'predix', label: 'Predix' },
        { key: 'maximoCmWos', label: 'MAXIMO (CM WOs)' }
    ];

    public readonly riskRegisterColumns: RiskRegisterColumn[] = [
        { key: 'rank', label: 'Rank' },
        { key: 'assetTag', label: 'Asset Tag' },
        { key: 'assetDescription', label: 'Asset Description' },
        { key: 'criticality', label: 'Asset Criticality' },
        { key: 'woStatus', label: 'WO Status' },
        { key: 'priority', label: 'Priority' },
        { key: 'riskType', label: 'Risk Type', dividerAfter: true },
        { key: 'failureType', label: 'Potential Failure Mode' },
        { key: 'rsl', label: 'RSL', dividerAfter: true },
        { key: 'stl', label: 'STL' },
        { key: 'iow', label: 'IOW' },
        { key: 'ord', label: 'ORD', dividerAfter: true },
        { key: 'predix', label: 'Predix' },
        { key: 'maximoCmWos', label: 'MAXIMO (CM WOs)', dividerAfter: true },
        { key: 'score', label: 'Risk Score', sortable: true },
        { key: 'potentialBadActor', label: 'Potential Reliability Risk', dividerAfter: true },
        // Hidden per request: Severity column temporarily disabled.
        // { key: 'severity', label: 'Severity', dividerAfter: true },
        { key: 'aiAdjustedRiskScore', label: 'AI-Adjusted Risk Score', dividerAfter: true, sortable: true },
        // Hidden per request: Trend column temporarily disabled.
        // { key: 'explTrend', label: 'Trend', dividerAfter: true },
        { key: 'risk_trend', label: 'Risk Trend (MoM)' }
    ];

    public riskRegisterRows: RiskRegisterRow[] = [];

    // TODO: Replace placeholder metric values with API response values.
    public readonly metricCards: HomeMetricCard[] = [
        { title: 'Asset Tags', value: '--', filterKey: 'allRows' },
        {
            title: 'Potential Reliability Risk',
            value: '--',
            filterKey: 'potentialReliabilityRisk',
            tooltip: "Denotes 'Bad Actor' assets. The candidacy rule is: (>=2 similar CM WOs/12 months OR >=3 different CM WOs/12 months) AND total CM cost > $50K. Indicates assets that qualify as potential Bad Actors: ≥2 similar or ≥3 distinct CM WOs within the past 12 months and total CM cost > $50K."
        },
        {
            title: 'Safety risk type',
            value: '--',
            filterKey: 'safetyRiskType',
            tooltip: 'Denotes Risk Type: Assets with failure scenarios that could impact personnel safety or create unsafe operating conditions.'
        },
        {
            title: 'Potential LPO risk type',
            value: '--',
            filterKey: 'potentialLpoRiskType',
            tooltip: 'Denotes Risk Type: Assets with failure scenarios that could result in a Loss of Primary Containment (LPO) and unintended release of process materials.'
        }
    ];

    public processEngineeringMonthlyReportResponse: MonthlyReportResponse | null = null;
    public monthlyReportIssueRows: MonthlyReportIssueRow[] = [];
    public monthlyEquipmentReferencedRows: MonthlyEquipmentReferencedRow[] = [];
    public monthlyItemsRequiringAttention: string[] = [];
    public monthlyOpportunities: string[] = [];

    private citationsManifest: CitationsManifestResponse | null = null;
    private isCitationsManifestLoading = false;
    private hasCitationsManifestLoadError = false;

    private readonly riskDetailSectionDefaults: Record<RiskDetailSectionKey, boolean> = {
        chronologicalWalkthrough: false,
        trendAnalysis: false,
        preventiveMaintenance: false,
        explainability: false,
        aiAdjustedRiskScore: false,
        sourceMentions: false
    };

    private readonly riskDetailExpandedByRow: Record<string, Record<RiskDetailSectionKey, boolean>> = {};
    private readonly riskDetailResponseByRow: Record<string, AssetDetailResponse> = {};
    private readonly riskDetailLoadingByRow: Record<string, boolean> = {};
    private readonly riskDetailLoadErrorByRow: Record<string, boolean> = {};
    private readonly riskDetailLoadCallbacks: Record<string, Array<() => void>> = {};
    private readonly riskDetailSourceMentionsByRow: Record<string, RiskDetailSourceMention[]> = {};
    private readonly riskDetailTooltipOpenByRow: Record<string, { mainConcern?: boolean; recommendedAction?: boolean }> = {};
    private readonly riskDetailSourceMentionsLoadingByRow: Record<string, boolean> = {};
    private readonly riskDetailSourceMentionsLoadErrorByRow: Record<string, boolean> = {};
    private readonly riskDetailSourceMentionsTotalByRow: Record<string, number> = {};
    private readonly riskDetailTrendActiveIndexByRow: Record<string, number> = {};

    private readonly riskEditsStorageKey = 'sentry.riskRegisterEdits';
    public isEditDialogOpen = false;
    public isEditDialogSaving = false;
    public isDeleteDialogDeleting = false;
    public editDialogRow: RiskRegisterRow | null = null;
    public editDialogForm: RiskEditForm = { mainConcern: '', recommendedAction: '', comment: '', riskType: '', failureType: '' };
    public isDeleteDialogOpen = false;
    public deleteDialogRow: RiskRegisterRow | null = null;
    public deleteDialogComment = '';
    public canViewRiskRegisterActions = false;
    public isViewPreviousActionsOpen = false;
    public isPreviousActionsFilterOpen = false;
    public previousActionsFilterSelection: Record<PreviousActionFilterKey, boolean> = {
        engineRe: true,
        mfgRe: true
    };
    public readonly previousActionsFilterOptions: Array<{ key: PreviousActionFilterKey; label: string }> = [
        { key: 'engineRe', label: 'Engine RE' },
        { key: 'mfgRe', label: 'MFG RE' }
    ];
    private readonly appSettingsStorageKey = '_AppSettings';
    private previousActionsFilterGroupIds: Record<PreviousActionFilterKey, string> = {
        engineRe: '',
        mfgRe: ''
    };
    private sentryDevUsersGroupId = '';
    private previousActionGroupMemberIdentityCache: Record<string, Set<string>> = {};
    private previousActionGroupMemberLoadState: Record<string, 'idle' | 'loading' | 'loaded' | 'error'> = {};
    private currentUserOid = '';
    private currentUserGroupIds: string[] = [];
    public riskTypeDropdown: RiskEditDropdownState = { open: false, search: '' };
    public failureTypeDropdown: RiskEditDropdownState = { open: false, search: '' };
    private readonly dropdownSearchThreshold = 6;
    private readonly approvalHistoryFieldOrder: Array<{ key: keyof AssetDetailApprovalStatus; label: string }> = [
        { key: 'risk_type', label: 'Risk Type' },
        { key: 'failure_type', label: 'Potential Failure Mode' },
        { key: 'main_concern', label: 'Main Concern' },
        { key: 'recommended_action', label: 'Recommended Action' }
    ];

    public readonly riskDetailExplainabilityFactors: RiskDetailFactor[] = [
        {
            label: 'Criticality (M 0.25)',
            value: '0.000',
            points: '0.0 pts',
            formula: 'Asset criticality (N=1, L=2, M=3, H=4)'
        },
        {
            label: 'Severity (N 0.2)',
            value: '0.000',
            points: '0.0 pts',
            formula: 'Avg symptom severity from LLM classification (0-1)'
        },
        {
            label: 'Recurrence (W 0.15)',
            value: '0.167',
            points: '2.5 pts',
            formula: 'min(event_count, 6) / 6'
        },
        {
            label: 'Recency (W 0.15)',
            value: '0.000',
            points: '0.0 pts',
            formula: 'max(0, 1 - days_since_last / 30)'
        },
        {
            label: 'Source Diversity (W 0.1)',
            value: '0.250',
            points: '2.5 pts',
            formula: 'min(distinct_sources, 4) / 4'
        },
        {
            label: 'Unresolved WO (W 0.1)',
            value: '0.000',
            points: '0.0 pts',
            formula: '1.0 if any open MAXIMO WO, else 0.0'
        },
        {
            label: 'Trend Worsening (W 0.05)',
            value: '0.000',
            points: '0.0 pts',
            formula: 'Score delta > 0 (determined from source trends)'
        }
    ];

    public readonly defaultRiskDetailSourceMentionsRows: RiskDetailSourceMention[] = [
        {
            source: 'ORD',
            date: '--',
            description: 'ORD mobile operator exception',
            repetitionTimes: '--',
            woStatus: '--',
            ref: 'CKV-FC/A11/V-12'
        }
    ];

    public get monthlyReport(): MonthlyReportItem | null {
        return this.processEngineeringMonthlyReportResponse?.reports?.[0] ?? null;
    }

    public get isDashboardView(): boolean {
        return this.currentLandingStage === 'dashboard';
    }

    public get selectedRefineryCard(): RefineryLandingCard | null {
        return this.getRefineryById(this.selectedRefineryId);
    }

    public get selectedUnitCard(): UnitLandingCard | null {
        const refinery = this.selectedRefineryCard;

        if (!refinery) {
            return null;
        }

        return refinery.units.find((unit) => unit.id === this.selectedUnitId) ?? null;
    }

    private readonly riskRegisterPageSizeStorageKey = 'RiskRegister.PageSize';

    constructor(
        @Optional() private homeApiService?: HomeApiService,
        @Optional() private route?: ActivatedRoute,
        @Optional() private router?: Router,
        @Optional() private appConfigService?: AppConfigService,
        @Optional() private graphService?: GraphService
    ) {
        this.resolvePreviousActionFilterGroups();
        this.resolveSentryDevUsersGroup();
        this.hydrateCurrentUserGroupContext();
        this.restoreRiskRegisterPageSizePreference();
    }

    public ngOnInit(): void {
        this.resolveRiskRegisterActionAccess();

        if (this.route) {
            this.route.paramMap.subscribe((params) => {
                const routeRunId = params.get('runId')?.trim() ?? '';
                const routeRefineryId = params.get('refineryId')?.trim() ?? '';
                const routeUnitId = params.get('unitId')?.trim() ?? '';

                this.pendingRouteSelection = { routeRefineryId, routeUnitId, routeRunId };
                this.applyPendingRouteSelectionIfReady();
            });

            this.loadLandingCards();
            return;
        }

        this.pendingRouteSelection = { routeRefineryId: '', routeUnitId: '', routeRunId: '' };
        this.loadLandingCards();
    }

    public ngOnDestroy(): void {
        this.stopPdfStatusPolling();
    }

    private restoreRiskRegisterPageSizePreference(): void {
        const storedPageSize = Number(Variables.get(this.riskRegisterPageSizeStorageKey, VariableType.Local));

        if (this.riskRegisterPageSizeOptions.includes(storedPageSize)) {
            this.riskRegisterPageSize = storedPageSize;
        }
    }

    private initializeRouteSelection(routeRefineryId: string, routeUnitId: string, routeRunId: string): void {
        const normalizedRefineryId = routeRefineryId.toLowerCase();
        const normalizedUnitId = routeUnitId.toLowerCase();
        const refinery = this.getRefineryById(normalizedRefineryId);
        const unit = refinery?.units.find((entry) => entry.id === normalizedUnitId) ?? null;

        this.selectedRefineryId = refinery?.id ?? '';
        this.selectedUnitId = unit?.id ?? '';

        // Keep legacy /run/:runId deep links working by resolving scope from landing hierarchy.
        if (routeRunId && (!this.selectedRefineryId || !this.selectedUnitId)) {
            const scopedSelection = this.findRefineryUnitByRunId(routeRunId);

            if (scopedSelection) {
                this.selectedRefineryId = scopedSelection.refineryId;
                this.selectedUnitId = scopedSelection.unitId;
            }
        }

        if (routeRunId) {
            this.currentLandingStage = 'dashboard';
            this.loadRunOptions();
            this.applyRunSelection(routeRunId, false);
            return;
        }

        this.applyRunSelection('', false);

        if (unit) {
            this.currentLandingStage = 'dashboard';
            this.loadRunOptionsForSelection();
            const latestRunId = this.runOptions[0]?.runId ?? '';
            this.applyRunSelection(latestRunId, false);
            return;
        }

        if (refinery) {
            this.currentLandingStage = 'unit';
            this.runOptions = [];
            return;
        }

        this.currentLandingStage = 'refinery';
        this.runOptions = [];
    }

    private loadLandingCards(): void {
        if (!this.homeApiService || typeof this.homeApiService.getRefineries !== 'function' || this.isLandingCardsLoading) {
            this.isLandingCardsReady = true;
            this.applyPendingRouteSelectionIfReady();
            return;
        }

        this.isLandingCardsLoading = true;
        this.hasLandingCardsLoadError = false;

        this.homeApiService.getRefineries<RefineryLandingResponse[]>().subscribe({
            next: (response) => {
                this.refineryLandingCards = this.mapRefineryLandingCards(response);
                this.isLandingCardsLoading = false;
                this.isLandingCardsReady = true;
                this.applyPendingRouteSelectionIfReady();
            },
            error: () => {
                this.refineryLandingCards = [];
                this.hasLandingCardsLoadError = true;
                this.isLandingCardsLoading = false;
                this.isLandingCardsReady = true;
                this.applyPendingRouteSelectionIfReady();
            }
        });
    }

    private applyPendingRouteSelectionIfReady(): void {
        if (!this.isLandingCardsReady || !this.pendingRouteSelection) {
            return;
        }

        const selection = this.pendingRouteSelection;
        this.pendingRouteSelection = null;
        this.initializeRouteSelection(selection.routeRefineryId, selection.routeUnitId, selection.routeRunId);
    }

    private loadRunOptionsForSelection(): void {
        if (this.selectedRefineryId && this.selectedUnitId) {
            this.runOptions = this.selectedUnitCard?.runs ?? [];
            this.hasRunOptionsLoadError = false;
            this.isRunOptionsLoading = false;
            return;
        }

        this.loadRunOptions();
    }

    public loadRunOptions(onLoaded?: (runOptions: RunOption[]) => void): void {
        if (this.selectedRefineryId && this.selectedUnitId) {
            this.runOptions = this.selectedUnitCard?.runs ?? [];
            this.hasRunOptionsLoadError = false;
            this.isRunOptionsLoading = false;
            onLoaded?.(this.runOptions);
            return;
        }

        if (!this.homeApiService || this.isRunOptionsLoading) {
            onLoaded?.(this.runOptions);
            return;
        }

        this.isRunOptionsLoading = true;
        this.hasRunOptionsLoadError = false;

        this.homeApiService.getRuns<RunOptionResponse[]>().subscribe({
            next: (response) => {
                this.runOptions = this.extractRunOptions(response);

                this.isRunOptionsLoading = false;
                onLoaded?.(this.runOptions);
            },
            error: () => {
                this.runOptions = [];
                this.selectedRun = '';
                this.hasRunOptionsLoadError = true;
                this.isRunOptionsLoading = false;
                onLoaded?.([]);
            }
        });
    }

    public onRunSelectionChange(runId: string): void {
        const nextRunId = runId?.trim() ?? '';

        if (this.router) {
            if (!nextRunId) {
                if (this.selectedRefineryId && this.selectedUnitId) {
                    this.router.navigate(['/refinery', this.selectedRefineryId, 'unit', this.selectedUnitId]);
                    return;
                }

                this.router.navigateByUrl('/');
                return;
            }

            if (this.selectedRefineryId && this.selectedUnitId) {
                this.router.navigate(['/refinery', this.selectedRefineryId, 'unit', this.selectedUnitId, 'run', nextRunId]);
                return;
            }

            this.router.navigate(['/run', nextRunId]);
            return;
        }

        this.applyRunSelection(nextRunId, true);
    }

    public onRefineryCardClick(refinery: RefineryLandingCard): void {
        if (!this.router) {
            return;
        }

        this.router.navigate(['/refinery', refinery.id]);
    }

    public onUnitCardClick(unit: UnitLandingCard): void {
        if (!this.router || !this.selectedRefineryId) {
            return;
        }

        this.router.navigate(['/refinery', this.selectedRefineryId, 'unit', unit.id]);
    }

    public onDownloadReportClick(): void {
        if (this.runSummaryRegenerationRequired) {
            this.isDownloadConfirmationDialogOpen = true;
            return;
        }

        this.downloadPdfReport();
    }

    public closeDownloadConfirmationDialog(): void {
        this.isDownloadConfirmationDialogOpen = false;
    }

    public onDownloadConfirmationYes(): void {
        this.closeDownloadConfirmationDialog();
    }

    public onDownloadConfirmationNo(): void {
        this.closeDownloadConfirmationDialog();
        this.downloadPdfReport();
    }

    private mapRefineryLandingCards(response: RefineryLandingResponse[]): RefineryLandingCard[] {
        const cards: RefineryLandingCard[] = [];

        for (const refineryEntry of response ?? []) {
            const refineryLabel = refineryEntry?.refinery?.trim();

            if (!refineryLabel) {
                continue;
            }

            const unitCards: UnitLandingCard[] = [];

            for (const unitEntry of refineryEntry.units ?? []) {
                const unitLabel = unitEntry?.unit?.trim();

                if (!unitLabel) {
                    continue;
                }

                unitCards.push({
                    id: unitLabel.toLowerCase(),
                    label: unitLabel,
                    imageUrl: 'assets/images/cvx_refining_icon_RGB_FA_1.png',
                    runs: this.extractRunOptions(unitEntry.runs ?? [])
                });
            }

            cards.push({
                id: refineryLabel.toLowerCase(),
                label: refineryLabel,
                imageUrl: 'assets/images/cvx_refining_icon_RGB_FA_1.png',
                units: unitCards
            });
        }

        return cards;
    }

    public downloadPdfReport(): void {
        if (!this.selectedRun || this.isPdfDownloading || this.isPdfRegenerationInProgress || !this.homeApiService) {
            return;
        }

        this.isPdfDownloading = true;

        this.homeApiService.getProcessSummaryPdf(this.selectedRun, this.selectedRefineryId, this.selectedUnitId).subscribe({
            next: (blob: Blob) => {
                // Create a temporary URL for the blob
                const url = window.URL.createObjectURL(blob);
                
                // Create a temporary anchor element to trigger download
                const anchor = document.createElement('a');
                anchor.href = url;

                anchor.download = this.getPdfDownloadFileName(this.selectedRun);
                
                // Trigger download
                document.body.appendChild(anchor);
                anchor.click();
                
                // Cleanup
                document.body.removeChild(anchor);
                window.URL.revokeObjectURL(url);
                
                this.isPdfDownloading = false;
            },
            error: (error) => {
                console.error('Error downloading PDF report:', error);
                this.isPdfDownloading = false;
                // You could show a user-friendly error message here
                alert('Failed to download report. Please try again.');
            }
        });
    }

    private getPdfDownloadFileName(runId: string): string {
        const sanitizedRunId = (runId ?? '').trim().replace(/[^a-zA-Z0-9_-]/g, '_');
        const safeRunId = sanitizedRunId || 'run';

        return `${safeRunId}_Reliability_Risk_Report.pdf`;
    }

    private applyRunSelection(runId: string, resetFilters: boolean): void {
        this.selectedRun = runId;
        this.closeDownloadConfirmationDialog();
        this.stopPdfStatusPolling();
        this.isPdfRegenerationInProgress = false;
        this.clearRiskDetailState();

        if (!this.selectedRun) {
            this.resetRunSummaryDisplay();
            this.resetMonthlyReportDisplay();
            this.resetRiskRegisterDisplay();
            this.resetCitationsManifestState();
            this.riskRegisterTotalResults = 0;

            if (resetFilters) {
                this.currentRiskRegisterPage = 1;
                this.clearAppliedFilters();
                this.riskRegisterSearchQuery = '';
            }

            return;
        }

        this.resetRunSummaryDisplay();
        this.resetMonthlyReportDisplay();
        this.resetRiskRegisterDisplay();
        this.resetCitationsManifestState();
        this.riskRegisterTotalResults = 0;

        this.loadRunSummary(this.selectedRun);
        this.loadRunAssets(this.selectedRun);
        this.loadMonthlyReport(this.selectedRun);
        this.loadCitationsManifest(this.selectedRun);

        if (resetFilters) {
            this.currentRiskRegisterPage = 1;
            this.clearAppliedFilters();
            this.riskRegisterSearchQuery = '';
        }
    }

    public get monthlyReportDisplayTitle(): string {
        if (!this.monthlyReport) {
            return '';
        }

        const fileLabel = this.formatReportFileName(this.monthlyReport.file);
        const monthYear = this.formatMonthYear(this.monthlyReport.date);

        return `${fileLabel} — ${monthYear}`;
    }

    public get monthlyReportSummary(): string {
        return this.monthlyReport?.summary ?? '';
    }

    public get monthlyReportKeyFindings(): string[] {
        return ((this.monthlyReport?.key_findings ?? []) as (MonthlyReportKeyFinding | string)[]).map((f) =>
            typeof f === 'string' ? f : [f.category, f.finding, f.reliability_impact].filter(Boolean).join(' — ')
        );
    }

    public get monthlyReportEmptyStateMessage(): string {
        if (!this.selectedRun) {
            return 'Select a run to view the monthly report.';
        }

        if (this.isMonthlyReportLoading) {
            return 'Loading monthly report...';
        }

        if (this.hasMonthlyReportLoadError) {
            return 'Unable to load monthly report. Please try again.';
        }

        const generatedLabel = this.runSummaryLastUpdatedLabel?.trim();
        return `Monthly Leading Indicators Report is not yet available for this run. The Process Engineering monthly report had not been published as of ${generatedLabel}. It will appear here once the report is issued.`;
        // return 'No monthly report data available for the selected run. Reason - Process Engineering monthly report is unavailable for current month';
    }

    public get monthlyIssueSummaryChips(): MonthlyIssueSummaryChip[] {
        const typeCounts = this.monthlyReportIssueRows.reduce<Record<string, number>>((accumulator, item) => {
            const normalizedType = this.normalizeIssueType(item.type);
            accumulator[normalizedType] = (accumulator[normalizedType] ?? 0) + 1;
            return accumulator;
        }, {});

        const failures = typeCounts.failure ?? 0;
        const trips = typeCounts.trip ?? 0;
        const delayUnderperformance = (typeCounts.underperformance ?? 0) + (typeCounts.delay ?? 0);
        const linkedFailureOpportunity = typeCounts['linked failure-opportunity'] ?? 0;
        const knownCategoryTotal = failures + trips + delayUnderperformance + linkedFailureOpportunity;
        const others = Math.max(0, this.monthlyReportIssueRows.length - knownCategoryTotal);

        return [
            { label: 'Failures', count: failures, tone: 'failure' },
            { label: 'Trip', count: trips, tone: 'trip' },
            { label: 'Delay/Underperformance', count: delayUnderperformance, tone: 'delay' },
            { label: 'Linked Failure+Opportunity', count: linkedFailureOpportunity, tone: 'linked' },
            { label: 'Other', count: others, tone: 'other' }
        ];
    }

    public getMonthlyIssueTypeClass(type: string): string {
        const normalizedType = this.normalizeIssueType(type);

        if (normalizedType === 'failure') {
            return 'issue-type-failure';
        }

        if (normalizedType === 'trip') {
            return 'issue-type-trip';
        }

        if (normalizedType === 'underperformance') {
            return 'issue-type-underperformance';
        }

        if (normalizedType === 'linked failure-opportunity') {
            return 'issue-type-linked';
        }

        return 'issue-type-other';
    }

    public toggleRiskRegister(): void {
        this.isRiskRegisterExpanded = !this.isRiskRegisterExpanded;
    }

    public toggleProcessEngineeringMonthlyReport(): void {
        this.isProcessEngineeringMonthlyReportExpanded = !this.isProcessEngineeringMonthlyReportExpanded;
    }

    public toggleRow(row: RiskRegisterRow): void {
        row.isExpanded = !row.isExpanded;

        if (row.isExpanded) {
            this.ensureRiskDetailStateForRow(row, true);
            this.loadRiskAssetDetail(row);
        }
    }

    public onEditRow(row: RiskRegisterRow): void {
        this.editDialogRow = row;
        this.isViewPreviousActionsOpen = false;
        this.resetPreviousActionsFilters();
        this.editDialogForm = {
            mainConcern: '',
            recommendedAction: '',
            comment: '',
            riskType: row.riskType,
            failureType: row.failureType
        };
        this.riskTypeDropdown = { open: false, search: '' };
        this.failureTypeDropdown = { open: false, search: '' };
        this.isEditDialogOpen = true;

        this.ensureRiskDetailStateForRow(row);
        this.loadRiskAssetDetail(row, () => this.populateEditDialogForm(row));
    }

    public onDeleteRow(row: RiskRegisterRow): void {
        if (row.isDeleted) {
            return;
        }

        this.deleteDialogRow = row;
        this.deleteDialogComment = '';
        this.isDeleteDialogDeleting = false;
        this.isDeleteDialogOpen = true;
    }

    public closeDeleteDialog(): void {
        this.isDeleteDialogDeleting = false;
        this.isDeleteDialogOpen = false;
        this.deleteDialogRow = null;
        this.deleteDialogComment = '';
    }

    public get canConfirmDeleteDialog(): boolean {
        return this.deleteDialogComment.trim().length > 0 && !this.isDeleteDialogDeleting;
    }

    public confirmDeleteDialog(): void {
        const row = this.deleteDialogRow;

        if (!row || this.isDeleteDialogDeleting) {
            return;
        }

        const trimmedComment = this.deleteDialogComment.trim();

        if (!trimmedComment) {
            alert('Comment is required to delete an asset.');
            return;
        }

        const runId = this.selectedRun?.trim();
        const refineryId = this.selectedRefineryId?.trim();
        const unitId = this.selectedUnitId?.trim();
        const canDeleteFromApi = Boolean(
            this.homeApiService &&
            runId &&
            refineryId &&
            unitId &&
            typeof this.homeApiService.deleteAsset === 'function'
        );

        if (!canDeleteFromApi) {
            this.closeDeleteDialog();
            return;
        }

        this.isDeleteDialogDeleting = true;

        this.homeApiService!.deleteAsset<DeleteAssetResponse>(runId!, refineryId!, unitId!, row.assetTag, trimmedComment).subscribe({
            next: (response) => {
                this.isDeleteDialogDeleting = false;
                const isSuccess = response?.success === true || response?.is_deleted === true;

                if (!isSuccess) {
                    alert(response?.message || 'Unable to delete asset. Please try again.');
                    return;
                }

                this.closeDeleteDialog();
                this.applyRunSelection(runId!, false);
            },
            error: () => {
                this.isDeleteDialogDeleting = false;
                alert('Unable to delete asset. Please try again.');
            }
        });
    }

    public closeEditDialog(): void {
        this.isEditDialogSaving = false;
        this.isViewPreviousActionsOpen = false;
        this.resetPreviousActionsFilters();
        this.isEditDialogOpen = false;
        this.editDialogRow = null;
    }

    public canViewPreviousActions(row: RiskRegisterRow): boolean {
        const detail = this.getRiskDetailResponse(row);
        return detail?.approval_status?.is_modified === true || this.isDeleteActionPresent(row, detail);
    }

    public toggleViewPreviousActions(row: RiskRegisterRow): void {
        if (!this.canViewPreviousActions(row)) {
            this.isViewPreviousActionsOpen = false;
            this.isPreviousActionsFilterOpen = false;
            return;
        }

        this.isViewPreviousActionsOpen = !this.isViewPreviousActionsOpen;

        if (!this.isViewPreviousActionsOpen) {
            this.isPreviousActionsFilterOpen = false;
            return;
        }

        this.ensureSelectedPreviousActionGroupsLoaded();
        this.ensureSentryDevUsersGroupLoaded();
    }

    public togglePreviousActionsFilterDropdown(): void {
        if (!this.isViewPreviousActionsOpen) {
            this.isPreviousActionsFilterOpen = false;
            return;
        }

        this.isPreviousActionsFilterOpen = !this.isPreviousActionsFilterOpen;
    }

    public isPreviousActionsFilterSelected(filterKey: PreviousActionFilterKey): boolean {
        return this.previousActionsFilterSelection[filterKey];
    }

    public onPreviousActionsFilterToggle(filterKey: PreviousActionFilterKey): void {
        this.previousActionsFilterSelection = {
            ...this.previousActionsFilterSelection,
            [filterKey]: !this.previousActionsFilterSelection[filterKey]
        };

        this.ensureSelectedPreviousActionGroupsLoaded();
    }

    public getFilteredHistoryEntries(entries: ViewActionHistoryEntry[]): ViewActionHistoryEntry[] {
        const hasAllFiltersSelected = this.previousActionsFilterSelection.engineRe && this.previousActionsFilterSelection.mfgRe;

        if (hasAllFiltersSelected) {
            return entries;
        }

        return entries.filter((entry) => this.matchesPreviousActionFilter(entry));
    }

    public shouldHideHistoryComment(entry: ViewActionHistoryEntry): boolean {
        const devGroupId = this.sentryDevUsersGroupId.trim().toLowerCase();

        if (!devGroupId) {
            return false;
        }

        const modifierGroupIds = this.getModifierGroupIds(entry);

        if (modifierGroupIds.length > 0) {
            return modifierGroupIds.includes(devGroupId);
        }

        const modifierIdentityCandidates = this.getModifierIdentityCandidates(entry);

        if (modifierIdentityCandidates.length === 0) {
            return false;
        }

        const groupState = this.previousActionGroupMemberLoadState[devGroupId] ?? 'idle';

        if (groupState === 'idle') {
            this.loadGroupMembersForFilterGroup(devGroupId);
            return false;
        }

        return this.isUserIdentityInGroup(devGroupId, modifierIdentityCandidates);
    }

    public hasVisiblePreviousActions(fieldHistories: ViewActionFieldHistory[]): boolean {
        return fieldHistories.some((fieldHistory) => this.getFilteredHistoryEntries(fieldHistory.entries).length > 0);
    }

    public getModifiedApprovalFieldHistories(row: RiskRegisterRow): ViewActionFieldHistory[] {
        const detail = this.getRiskDetailResponse(row);
        const approvalStatus = detail?.approval_status;

        const modifiedFields = approvalStatus?.is_modified === true
            ? this.approvalHistoryFieldOrder
                .map(({ key, label }) => {
                    const field = approvalStatus[key] as AssetDetailApprovalFieldStatus | undefined;

                    if (!field || typeof field !== 'object' || field.is_modified !== true) {
                        return null;
                    }

                    const entries = this.getSortedApprovalHistoryEntries(field.modification_history).map((entry) => ({
                        value: this.formatApprovalHistoryValue(entry.value),
                        actionType: this.toDisplayValue(entry.action_type),
                        modifiedBy: this.toDisplayValue(entry.modified_by),
                        rawModifiedBy: this.toDisplayValue(entry.modified_by),
                        modifiedAt: this.formatDateOnly(entry.modified_at),
                        modifiedAtEpoch: this.toEpoch(entry.modified_at),
                        comment: this.toDisplayValue(entry.comment),
                        modifiedByGroupIds: this.extractHistoryEntryGroupIds(entry)
                    }));

                    return {
                        fieldLabel: label,
                        originalValue: this.formatApprovalHistoryValue(field.original_value),
                        currentValue: this.formatApprovalHistoryValue(field.current_value),
                        modifiedAtEpoch: this.toEpoch(field.modified_at),
                        entries
                    } as ViewActionFieldHistory;
                })
                .filter((field): field is ViewActionFieldHistory => field !== null)
            : [];

        const deleteActionHistory = this.getDeleteActionFieldHistory(row, detail);

        if (deleteActionHistory) {
            modifiedFields.push(deleteActionHistory);
        }

        return modifiedFields.sort((left, right) => {
            const epochDiff = left.modifiedAtEpoch - right.modifiedAtEpoch;

            if (epochDiff !== 0) {
                return epochDiff;
            }

            return left.fieldLabel.localeCompare(right.fieldLabel);
        });
    }

    public getHistoryStageSummary(entry: ViewActionHistoryEntry): string {
        const action = (entry.actionType || '').trim().toLowerCase();

        if (!action || action === '--') {
            return entry.value;
        }

        if (action === 'modify') {
            return `Modified to ${entry.value}`;
        }

        if (action === 'confirm') {
            return `Confirmed value ${entry.value}`;
        }

        if (action === 'delete') {
            return 'Asset deleted';
        }

        const normalizedAction = action.charAt(0).toUpperCase() + action.slice(1);
        return `${normalizedAction} to ${entry.value}`;
    }

    private isDeleteActionPresent(row: RiskRegisterRow, detail?: AssetDetailResponse | null): boolean {
        return Boolean(detail?.is_deleted ?? row.isDeleted);
    }

    private getDeleteActionFieldHistory(row: RiskRegisterRow, detail?: AssetDetailResponse | null): ViewActionFieldHistory | null {
        if (!this.isDeleteActionPresent(row, detail)) {
            return null;
        }

        const deletedAt = detail?.deleted_at ?? row.deletedAt ?? null;
        const deletedBy = detail?.deleted_by_name ?? row.deletedByName ?? null;
        const deletedComment = detail?.deleted_comment ?? row.deletedComment ?? null;
        const deletedAtEpoch = this.toEpoch(deletedAt);

        return {
            fieldLabel: 'Delete Action',
            originalValue: 'Active',
            currentValue: 'Deleted',
            modifiedAtEpoch: deletedAtEpoch,
            entries: [
                {
                    value: 'Deleted',
                    actionType: 'delete',
                    modifiedBy: this.toDisplayValue(deletedBy),
                    rawModifiedBy: this.toDisplayValue(deletedBy),
                    modifiedAt: this.formatDateOnly(deletedAt),
                    modifiedAtEpoch: deletedAtEpoch,
                    comment: this.toDisplayValue(deletedComment),
                    modifiedByGroupIds: this.extractGroupIdsFromUnknown(detail as unknown)
                }
            ]
        };
    }

    public getHistoryStageNumber(row: RiskRegisterRow, entry: ViewActionHistoryEntry): number {
        const uniqueSortedEpochs = Array.from(
            new Set(
                this.getModifiedApprovalFieldHistories(row)
                    .flatMap((fieldHistory) => fieldHistory.entries.map((fieldEntry) => fieldEntry.modifiedAtEpoch))
            )
        ).sort((left, right) => left - right);

        const stageIndex = uniqueSortedEpochs.indexOf(entry.modifiedAtEpoch);

        return stageIndex >= 0 ? stageIndex + 1 : 1;
    }

    public getRiskTypeOptions(row: RiskRegisterRow): string[] {
        const detail = this.getRiskDetailResponse(row);
        const rawOptions = detail?.approval_dropdowns?.risk_type ?? [];
        return Array.from(new Set(rawOptions));
    }

    public getFailureTypeOptions(row: RiskRegisterRow): string[] {
        const detail = this.getRiskDetailResponse(row);
        const rawOptions = detail?.approval_dropdowns?.failure_type ?? [];
        return Array.from(new Set(rawOptions));
    }

    public getFilteredDropdownOptions(options: string[], search: string): string[] {
        const query = search.trim().toLowerCase();

        if (!query) {
            return options.length > this.dropdownSearchThreshold ? options.slice(0, this.dropdownSearchThreshold) : options;
        }

        return options.filter((option) => option.toLowerCase().includes(query));
    }

    public toggleRiskTypeDropdown(): void {
        const nextOpen = !this.riskTypeDropdown.open;
        this.failureTypeDropdown.open = false;
        this.riskTypeDropdown = { open: nextOpen, search: '' };
    }

    public toggleFailureTypeDropdown(): void {
        const nextOpen = !this.failureTypeDropdown.open;
        this.riskTypeDropdown.open = false;
        this.failureTypeDropdown = { open: nextOpen, search: '' };
    }

    public selectRiskType(value: string): void {
        this.editDialogForm.riskType = value;
        this.riskTypeDropdown.open = false;
    }

    public selectFailureType(value: string): void {
        this.editDialogForm.failureType = value;
        this.failureTypeDropdown.open = false;
    }

    public closeAllDropdowns(): void {
        this.riskTypeDropdown.open = false;
        this.failureTypeDropdown.open = false;
    }

    public onEditDialogClick(event: MouseEvent): void {
        event.stopPropagation();
        const target = event.target as HTMLElement;

        if (!target.closest('.risk-edit-dropdown')) {
            this.closeAllDropdowns();
        }

        if (!target.closest('.risk-edit-history-filter')) {
            this.isPreviousActionsFilterOpen = false;
        }
    }

    private resetPreviousActionsFilters(): void {
        this.isPreviousActionsFilterOpen = false;
        this.previousActionsFilterSelection = {
            engineRe: true,
            mfgRe: true
        };
    }

    private matchesPreviousActionFilter(entry: ViewActionHistoryEntry): boolean {
        const selectedFilterGroupIds = this.getSelectedPreviousActionFilterGroupIds();

        if (selectedFilterGroupIds.length === 0) {
            return false;
        }

        const modifierGroupIds = this.getModifierGroupIds(entry);

        if (modifierGroupIds.length === 0) {
            const modifierIdentityCandidates = this.getModifierIdentityCandidates(entry);

            if (modifierIdentityCandidates.length === 0) {
                return false;
            }

            return selectedFilterGroupIds.some((groupId) => this.isUserIdentityInGroup(groupId, modifierIdentityCandidates));
        }

        return modifierGroupIds.some((groupId) => selectedFilterGroupIds.includes(groupId));
    }

    private resolvePreviousActionFilterGroups(): void {
        const engineGroupId = String(this.appConfigService?.getConfig('sentry_engine_re') ?? '').trim();
        const mfgGroupId = String(this.appConfigService?.getConfig('sentry_mfg_re') ?? '').trim();

        this.previousActionsFilterGroupIds = {
            engineRe: engineGroupId,
            mfgRe: mfgGroupId
        };
    }

    private resolveSentryDevUsersGroup(): void {
        this.sentryDevUsersGroupId = String(this.appConfigService?.getConfig('sentry_dev_users') ?? '').trim();
    }

    private resolveRiskRegisterActionAccess(): void {
        const groupIds = [
            ...Object.values(this.previousActionsFilterGroupIds),
            this.sentryDevUsersGroupId
        ]
            .map((groupId) => groupId.trim().toLowerCase())
            .filter((groupId) => groupId.length > 0);

        if (groupIds.length === 0) {
            this.canViewRiskRegisterActions = false;
            return;
        }

        const isInCachedGroups = this.currentUserGroupIds
            .map((groupId) => groupId.toLowerCase())
            .some((groupId) => groupIds.includes(groupId));

        if (isInCachedGroups) {
            this.canViewRiskRegisterActions = true;
            return;
        }

        if (!this.graphService) {
            this.canViewRiskRegisterActions = false;
            return;
        }

        Promise.all(groupIds.map((groupId) => this.graphService!.checkUserInGroup(groupId)))
            .then((results) => {
                this.canViewRiskRegisterActions = results.some((isMember) => isMember === true);
            })
            .catch(() => {
                this.canViewRiskRegisterActions = false;
            });
    }

    private hydrateCurrentUserGroupContext(): void {
        try {
            const rawAppSettings = sessionStorage.getItem(this.appSettingsStorageKey);

            if (!rawAppSettings) {
                return;
            }

            const parsed = JSON.parse(rawAppSettings) as {
                user?: {
                    oid?: string;
                    idTokenClaims?: {
                        oid?: string;
                        groups?: unknown;
                    };
                    roles?: unknown;
                };
            };

            const user = parsed?.user;
            const userOid = String(user?.oid ?? user?.idTokenClaims?.oid ?? '').trim();
            const idTokenGroups = this.normalizeGroupIds((user?.idTokenClaims?.groups as unknown[]) ?? []);
            const userRoles = this.normalizeGroupIds((user?.roles as unknown[]) ?? []);

            this.currentUserOid = userOid;
            this.currentUserGroupIds = Array.from(new Set([...idTokenGroups, ...userRoles]));
        } catch {
            this.currentUserOid = '';
            this.currentUserGroupIds = [];
        }
    }

    private getSelectedPreviousActionFilterGroupIds(): string[] {
        return this.previousActionsFilterOptions
            .filter((option) => this.previousActionsFilterSelection[option.key])
            .map((option) => this.previousActionsFilterGroupIds[option.key])
            .map((groupId) => groupId.trim().toLowerCase())
            .filter((groupId) => groupId.length > 0);
    }

    private getModifierGroupIds(entry: ViewActionHistoryEntry): string[] {
        const explicitGroupIds = this.normalizeGroupIds(entry.modifiedByGroupIds);

        if (explicitGroupIds.length > 0) {
            return explicitGroupIds;
        }

        const rawModifier = entry.rawModifiedBy.trim().toLowerCase();

        if (rawModifier && this.currentUserOid && rawModifier === this.currentUserOid.toLowerCase()) {
            return this.currentUserGroupIds.map((groupId) => groupId.toLowerCase());
        }

        return [];
    }

    private getModifierIdentityCandidates(entry: ViewActionHistoryEntry): string[] {
        const rawModifier = entry.rawModifiedBy.trim().toLowerCase();
        const displayModifier = entry.modifiedBy.trim().toLowerCase();
        const candidates = [rawModifier, displayModifier].filter((value) => value.length > 0);

        return Array.from(new Set(candidates));
    }

    private ensureSelectedPreviousActionGroupsLoaded(): void {
        const selectedFilterGroupIds = this.getSelectedPreviousActionFilterGroupIds();

        selectedFilterGroupIds.forEach((groupId) => {
            const state = this.previousActionGroupMemberLoadState[groupId] ?? 'idle';

            if (state === 'loading' || state === 'loaded') {
                return;
            }

            this.loadGroupMembersForFilterGroup(groupId);
        });
    }

    private ensureSentryDevUsersGroupLoaded(): void {
        const groupId = this.sentryDevUsersGroupId.trim().toLowerCase();

        if (!groupId) {
            return;
        }

        const state = this.previousActionGroupMemberLoadState[groupId] ?? 'idle';

        if (state === 'loading' || state === 'loaded') {
            return;
        }

        this.loadGroupMembersForFilterGroup(groupId);
    }

    private async loadGroupMembersForFilterGroup(groupId: string): Promise<void> {
        if (!this.graphService || !groupId) {
            return;
        }

        this.previousActionGroupMemberLoadState[groupId] = 'loading';

        try {
            const members = await this.graphService.getGroupMembers(groupId);
            const identitySet = new Set<string>();

            members.forEach((member) => {
                this.addIdentityCandidate(identitySet, member.id);
                this.addIdentityCandidate(identitySet, member.userPrincipalName);
                this.addIdentityCandidate(identitySet, member.mail);
                this.addIdentityCandidate(identitySet, member.displayName);
            });

            this.previousActionGroupMemberIdentityCache[groupId] = identitySet;
            this.previousActionGroupMemberLoadState[groupId] = 'loaded';
        } catch {
            this.previousActionGroupMemberIdentityCache[groupId] = new Set<string>();
            this.previousActionGroupMemberLoadState[groupId] = 'error';
        }
    }

    private isUserIdentityInGroup(groupId: string, identityCandidates: string[]): boolean {
        const memberIdentitySet = this.previousActionGroupMemberIdentityCache[groupId];

        if (!memberIdentitySet || memberIdentitySet.size === 0) {
            return false;
        }

        return identityCandidates.some((identity) => memberIdentitySet.has(identity));
    }

    private addIdentityCandidate(target: Set<string>, value?: string): void {
        const normalized = String(value ?? '').trim().toLowerCase();

        if (normalized) {
            target.add(normalized);
        }
    }

    private extractHistoryEntryGroupIds(entry: AssetDetailApprovalHistoryEntry): string[] {
        return this.extractGroupIdsFromUnknown(entry as unknown);
    }

    private extractGroupIdsFromUnknown(value: unknown): string[] {
        if (!value || typeof value !== 'object') {
            return [];
        }

        const record = value as Record<string, unknown>;
        const candidateKeys = [
            'modified_by_group_ids',
            'modifiedByGroupIds',
            'group_ids',
            'groupIds',
            'groups'
        ];

        for (const key of candidateKeys) {
            const groupIds = this.normalizeGroupIds(record[key]);

            if (groupIds.length > 0) {
                return groupIds;
            }
        }

        return [];
    }

    private normalizeGroupIds(value: unknown): string[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return value
            .map((item) => String(item ?? '').trim().toLowerCase())
            .filter((item) => item.length > 0);
    }

    public onRegenerateClick(): void {
        if (!this.selectedRun || !this.homeApiService || this.isPdfRegenerating) {
            return;
        }

        const runId = this.selectedRun;
        this.isPdfRegenerating = true;

        this.homeApiService.regeneratePdf<PdfRegenerationTriggerResponse>(runId).subscribe({
            next: (response) => {
                this.isPdfRegenerating = false;

                this.applyPdfRegenerationStatus(response?.status);

                if (response?.status?.regeneration_in_progress || response?.regeneration_started) {
                    this.startPdfStatusPolling(runId);
                }
            },
            error: () => {
                this.isPdfRegenerating = false;
                alert('Failed to regenerate report. Please try again.');
            }
        });
    }

    private startPdfStatusPolling(runId: string): void {
        this.stopPdfStatusPolling();

        const pollOnce = (): void => {
            if (!this.homeApiService || !this.selectedRun || this.selectedRun !== runId) {
                this.stopPdfStatusPolling();
                return;
            }

            this.homeApiService.getPdfStatus<PdfRegenerationStatusResponse>(runId, this.selectedRefineryId, this.selectedUnitId).subscribe({
                next: (statusResponse) => {
                    this.applyPdfRegenerationStatus(statusResponse);

                    if (!this.isPdfRegenerationInProgress) {
                        this.stopPdfStatusPolling();
                    }
                },
                error: () => {
                    // Keep polling on transient status endpoint errors.
                }
            });
        };

        this.pdfStatusPollTimer = setInterval(pollOnce, this.pdfStatusPollIntervalMs);
        pollOnce();
    }

    private stopPdfStatusPolling(): void {
        if (this.pdfStatusPollTimer !== null) {
            clearInterval(this.pdfStatusPollTimer);
            this.pdfStatusPollTimer = null;
        }
    }

    private applyPdfRegenerationStatus(status?: PdfRegenerationStatusResponse): void {
        if (!status) {
            return;
        }

        const wasRegenerationInProgress = this.isPdfRegenerationInProgress;

        this.isPdfRegenerationInProgress = Boolean(status.regeneration_in_progress);

        if (typeof status.regeneration_required === 'boolean') {
            this.runSummaryRegenerationRequired = status.regeneration_required;
        }

        if (wasRegenerationInProgress && !this.isPdfRegenerationInProgress) {
            this.refreshMonthlyReportAfterRegeneration();
        }
    }

    private refreshMonthlyReportAfterRegeneration(): void {
        if (!this.selectedRun || !this.homeApiService || typeof this.homeApiService.getProcessSummary !== 'function') {
            return;
        }

        this.loadMonthlyReport(this.selectedRun);
    }

    public saveEditDialog(): void {
        const row = this.editDialogRow;

        if (!row || this.isEditDialogSaving) {
            return;
        }

        const applyLocalEdits = (): void => {
            const rowKey = this.getRiskDetailRowKey(row);
            const edits = this.getStoredRiskEdits();
            edits[rowKey] = { ...this.editDialogForm };
            this.setStoredRiskEdits(edits);

            row.riskType = this.editDialogForm.riskType;
            row.failureType = this.editDialogForm.failureType;
        };

        const canSaveToApi = Boolean(
            this.homeApiService &&
            this.selectedRun &&
            this.selectedRefineryId &&
            this.selectedUnitId &&
            typeof this.homeApiService.updateAssetApprovals === 'function'
        );

        if (!canSaveToApi) {
            applyLocalEdits();
            this.closeEditDialog();
            return;
        }

        const runId = this.selectedRun;
        const refineryId = this.selectedRefineryId;
        const unitId = this.selectedUnitId;
        const remarks = this.buildEditDialogRemarks();
        const failureType = this.parseFailureTypeSelection(this.editDialogForm.failureType);
        const payload: BulkAssetApprovalRequest = {
            remarks,
            risk_type: {
                action: { action_type: 'modify', comment: remarks },
                risk_type: this.editDialogForm.riskType
            },
            failure_type: {
                action: { action_type: 'modify', comment: remarks },
                failure_mode_code: failureType.code,
                failure_mode_description: failureType.description
            },
            main_concern: {
                action: { action_type: 'modify', comment: remarks },
                main_concern: this.editDialogForm.mainConcern
            },
            recommended_action: {
                action: { action_type: 'modify', comment: remarks },
                recommended_action: this.editDialogForm.recommendedAction
            }
        };

        this.isEditDialogSaving = true;

        this.homeApiService!.updateAssetApprovals<BulkAssetApprovalResponse>(runId, refineryId, unitId, row.assetTag, payload).subscribe({
            next: (response) => {
                this.isEditDialogSaving = false;

                if (!response?.success) {
                    alert(response?.message || 'Unable to save edits. Please try again.');
                    return;
                }

                applyLocalEdits();
                this.closeEditDialog();
                this.applyRunSelection(runId, false);
            },
            error: () => {
                this.isEditDialogSaving = false;
                alert('Unable to save edits. Please try again.');
            }
        });
    }

    private buildEditDialogRemarks(): string {
        const comment = this.editDialogForm.comment?.trim();
        return comment || 'Updated from the ranked risk register edit dialog.';
    }

    private parseFailureTypeSelection(value: string): { code: string; description: string } {
        const trimmed = value.trim();

        if (!trimmed) {
            return { code: '', description: '' };
        }

        const separatorIndex = trimmed.indexOf(' - ');

        if (separatorIndex <= 0) {
            return {
                code: trimmed,
                description: trimmed
            };
        }

        const code = trimmed.slice(0, separatorIndex).trim();
        const description = trimmed.slice(separatorIndex + 3).trim();

        return {
            code: code || trimmed,
            description: description || trimmed
        };
    }

    public toggleRiskDetailSection(row: RiskRegisterRow, section: RiskDetailSectionKey): void {
        this.ensureRiskDetailStateForRow(row);
        const rowKey = this.getRiskDetailRowKey(row);
        const nextExpanded = !this.riskDetailExpandedByRow[rowKey][section];

        this.riskDetailExpandedByRow[rowKey][section] = nextExpanded;

        if (section === 'sourceMentions' && nextExpanded) {
            this.loadRiskSourceMentions(row);
        }
    }

    public isRiskDetailSectionExpanded(row: RiskRegisterRow, section: RiskDetailSectionKey): boolean {
        this.ensureRiskDetailStateForRow(row);
        return this.riskDetailExpandedByRow[this.getRiskDetailRowKey(row)][section];
    }

    public isRiskDetailLoading(row: RiskRegisterRow): boolean {
        return this.riskDetailLoadingByRow[this.getRiskDetailRowKey(row)] ?? false;
    }

    public hasRiskDetailLoadError(row: RiskRegisterRow): boolean {
        return this.riskDetailLoadErrorByRow[this.getRiskDetailRowKey(row)] ?? false;
    }

    public getRiskDetailTopSummary(row: RiskRegisterRow): RiskDetailTopSummary {
        const detail = this.getRiskDetailResponse(row);

        return {
            assetTag: this.toDisplayValue(detail?.asset_tag ?? row.assetTag),
            riskType: this.toDisplayValue(detail?.risk_type ?? row.riskType),
            criticality: this.toDisplayValue(detail?.criticality ?? row.criticality),
            score: this.toDisplayValue(detail?.score_100 ?? row.score)
        };
    }

    public getRiskDetailNarrativePoints(row: RiskRegisterRow): string[] {
        const detail = this.getRiskDetailResponse(row);
        return this.toBulletPoints(detail?.narrative);
    }

    public getRiskDetailMainConcern(row: RiskRegisterRow): string {
        const override = this.getStoredRiskEditForRow(row)?.mainConcern;

        if (override) {
            return override;
        }

        const detail = this.getRiskDetailResponse(row);
        return this.toDisplayValue(detail?.main_concern);
    }

    public getRiskDetailRecommendedAction(row: RiskRegisterRow): string {
        const override = this.getStoredRiskEditForRow(row)?.recommendedAction;

        if (override) {
            return override;
        }

        const detail = this.getRiskDetailResponse(row);
        return this.toDisplayValue(detail?.recommended_action);
    }

    public getRiskDetailMainConcernReasoning(row: RiskRegisterRow): string {
        const detail = this.getRiskDetailResponse(row);
        const reasoning = detail?.main_concern_reasoning?.trim();
        return reasoning || 'No AI reasoning is there for this';
    }

    public getRiskDetailRecommendedActionReasoning(row: RiskRegisterRow): string {
        const detail = this.getRiskDetailResponse(row);
        const reasoning = detail?.recommended_action_reasoning?.trim();
        return reasoning || 'No AI reasoning is there for this';
    }

    public toggleRiskDetailTooltip(row: RiskRegisterRow, section: 'mainConcern' | 'recommendedAction'): void {
        const key = this.getRiskDetailRowKey(row);
        if (!this.riskDetailTooltipOpenByRow[key]) {
            this.riskDetailTooltipOpenByRow[key] = {};
        }
        // Close other tooltip and toggle this one
        const wasOpen = this.riskDetailTooltipOpenByRow[key][section];
        this.riskDetailTooltipOpenByRow[key] = {};
        this.riskDetailTooltipOpenByRow[key][section] = !wasOpen;
    }

    public isRiskDetailTooltipOpen(row: RiskRegisterRow, section: 'mainConcern' | 'recommendedAction'): boolean {
        return this.riskDetailTooltipOpenByRow[this.getRiskDetailRowKey(row)]?.[section] ?? false;
    }

    public getRiskDetailChronologicalPoints(row: RiskRegisterRow): string[] {
        const detail = this.getRiskDetailResponse(row);
        return this.toBulletPoints(detail?.chronological_walkthrough);
    }

    public getRiskDetailTrendPoints(row: RiskRegisterRow): string[] {
        const detail = this.getRiskDetailResponse(row);
        return this.toBulletPoints(detail?.trend_summary);
    }

    public getRiskDetailTrendTimeline(row: RiskRegisterRow): RiskDetailTrendPoint[] {
        const points = this.getRiskDetailTrendPoints(row)
            .map((point) => this.parseTrendPoint(point))
            .filter((point) => Boolean(point.content));

        return this.getFirstTrendPointPerMonth(points, this.getAllowedTrendMonthKeysForSelectedRun());
    }

    public getRiskDetailActiveTrendIndex(row: RiskRegisterRow): number {
        const rowKey = this.getRiskDetailRowKey(row);
        const points = this.getRiskDetailTrendTimeline(row);

        if (points.length === 0) {
            return -1;
        }

        const storedIndex = this.riskDetailTrendActiveIndexByRow[rowKey];

        if (storedIndex === undefined || storedIndex < 0 || storedIndex >= points.length) {
            this.riskDetailTrendActiveIndexByRow[rowKey] = 0;
            return 0;
        }

        return storedIndex;
    }

    public onRiskDetailTrendDotClick(row: RiskRegisterRow, index: number): void {
        const points = this.getRiskDetailTrendTimeline(row);

        if (index < 0 || index >= points.length) {
            return;
        }

        this.riskDetailTrendActiveIndexByRow[this.getRiskDetailRowKey(row)] = index;
    }

    public getRiskDetailActiveTrendPoint(row: RiskRegisterRow): RiskDetailTrendPoint | null {
        const points = this.getRiskDetailTrendTimeline(row);
        const activeIndex = this.getRiskDetailActiveTrendIndex(row);

        if (activeIndex < 0 || activeIndex >= points.length) {
            return null;
        }

        return points[activeIndex];
    }

    public getRiskDetailPmMentions(row: RiskRegisterRow): string[] {
        const detail = this.getRiskDetailResponse(row);
        const mentions = detail?.pm_wo_mentions ?? [];

        return mentions
            .map((mention) => this.toPmMention(mention))
            .filter((mention) => Boolean(mention));
    }

    public getRiskDetailExplainabilityHeader(row: RiskRegisterRow): string {
        const detail = this.getRiskDetailResponse(row);
        const scoreValue = detail?.explainability?.score ?? detail?.explainability_score;
        const factorsCount = detail?.explainability?.factors?.length ?? this.riskDetailExplainabilityFactors.length;
        const scoreLabel = scoreValue === null || scoreValue === undefined ? '--' : this.formatDecimal(scoreValue, 4);

        return `Explainability Score: ${scoreLabel} (${factorsCount}-factor breakdown)`;
    }

    public getRiskDetailExplainabilityFactors(row: RiskRegisterRow): RiskDetailFactor[] {
        const detail = this.getRiskDetailResponse(row);
        const apiFactors = detail?.explainability?.factors ?? [];

        if (apiFactors.length === 0) {
            return this.riskDetailExplainabilityFactors;
        }

        return apiFactors.map((factor) => ({
            label: `${this.toDisplayValue(factor.label)} (W=${this.formatWeight(factor.weight)})`,
            value: this.formatDecimal(factor.value, 3),
            points: `${this.formatDecimal(factor.contribution_pts, 2)} pts`,
            formula: this.toDisplayValue(factor.formula)
        }));
    }

    public getRiskDetailAiAdjustedHeader(row: RiskRegisterRow): string {
        return `AI-Adjusted Risk Score: ${this.getRiskDetailAiAdjustedScore(row)}`;
    }

    public getRiskDetailAiAdjustedScore(row: RiskRegisterRow): string {
        const detail = this.getRiskDetailResponse(row);
        const score = detail?.ai_adjusted_risk_score;

        return score === null || score === undefined
            ? this.toDisplayValue(row.aiAdjustedRiskScore)
            : this.formatDecimal(score, 2);
    }

    public getRiskDetailSemanticRiskScore(row: RiskRegisterRow): string {
        const detail = this.getRiskDetailResponse(row);
        const score = detail?.semantic_risk_score;

        return score === null || score === undefined
            ? this.toDisplayValue(row.semanticRiskScore)
            : this.formatDecimal(score, 2);
    }

    public getRiskDetailAiAdjustedCalculationPoints(row: RiskRegisterRow): string[] {
        const detail = this.getRiskDetailResponse(row);
        const detailPoints = this.toBulletPoints(detail?.ai_adjusted_risk_calculation ?? undefined);

        if (detailPoints.length > 0) {
            return detailPoints;
        }

        return row.aiAdjustedRiskCalculation ?? [];
    }

    public getRiskDetailSemanticRationale(row: RiskRegisterRow): string {
        const detail = this.getRiskDetailResponse(row);
        const rationale = (detail?.rationale ?? detail?.semantic_risk_reason ?? row.semanticRationale ?? '').trim();

        return rationale || 'No rationale available for this asset.';
    }

    public getRiskDetailSemanticSupportingEvidence(row: RiskRegisterRow): string[] {
        const detail = this.getRiskDetailResponse(row);
        const evidence = this.toSemanticSupportingEvidence(detail?.semantic_supporting_evidence);

        return evidence.length > 0 ? evidence : row.semanticSupportingEvidence;
    }

    public getRiskDetailSourceMentionsLabel(row: RiskRegisterRow): string {
        return `Show all source mentions (chronological)`;
    }

    public getRiskDetailSourceMentionsRows(row: RiskRegisterRow): RiskDetailSourceMention[] {
        return this.riskDetailSourceMentionsByRow[this.getRiskDetailRowKey(row)] ?? this.defaultRiskDetailSourceMentionsRows;
    }

    public isRiskSourceMentionsLoading(row: RiskRegisterRow): boolean {
        return this.riskDetailSourceMentionsLoadingByRow[this.getRiskDetailRowKey(row)] ?? false;
    }

    public hasRiskSourceMentionsLoadError(row: RiskRegisterRow): boolean {
        return this.riskDetailSourceMentionsLoadErrorByRow[this.getRiskDetailRowKey(row)] ?? false;
    }

    public onCitationsClick(row: RiskRegisterRow): void {
        const assetTag = row.assetTag?.trim();

        if (!assetTag) {
            console.warn('[Citations] Missing asset tag for selected row.');
            return;
        }

        if (this.isCitationsManifestLoading) {
            console.info(`[Citations] Manifest still loading for run ${this.selectedRun}. Please wait.`);
            return;
        }

        if (!this.citationsManifest) {
            console.warn(`[Citations] Manifest is not available for run ${this.selectedRun}.`);
            return;
        }

        if (this.router && this.selectedRun) {
            const runScope = this.findRefineryUnitByRunId(this.selectedRun);
            const refineryId = runScope?.refineryId ?? '';
            const unitId = runScope?.unitId ?? '';

            if (!refineryId || !unitId) {
                console.warn(`[Citations] Missing refinery/unit scope for run ${this.selectedRun}.`);
                return;
            }

            const urlTree = this.router.createUrlTree(['/refinery', refineryId, 'unit', unitId, 'run', this.selectedRun, 'citations', assetTag]);
            const url = this.router.serializeUrl(urlTree);
            window.open(url, '_blank');
            return;
        }

        const byAsset = this.citationsManifest.by_asset ?? {};
        const citations = this.citationsManifest.citations ?? {};
        const citationIds = byAsset[assetTag] ?? [];

        const citationDetails = citationIds.map((citationId) => {
            const detail = citations[citationId];

            if (detail) {
                return detail;
            }

            return {
                citation_id: citationId,
                missing_in_manifest: true
            };
        });

        console.groupCollapsed(`[Citations] ${assetTag} (${citationIds.length} ids)`);
        console.log('run_id:', this.selectedRun);
        console.log('asset_tag:', assetTag);
        console.log('citation_ids:', citationIds);
        console.log('citation_details:', citationDetails);
        console.groupEnd();
    }

    public get totalRiskRegisterPages(): number {
        return Math.max(1, Math.ceil(this.filteredRiskRegisterRows.length / this.riskRegisterPageSize));
    }

    public get riskRegisterTableMaxHeight(): string {
        const estimatedRowHeight = 56;
        const headerHeight = 43;
        const tableHeight = (this.riskRegisterPageSize * estimatedRowHeight) + headerHeight;

        return `min(calc(100vh - 210px), ${tableHeight}px)`;
    }

    public get paginatedRiskRegisterRows(): RiskRegisterRow[] {
        const start = (this.currentRiskRegisterPage - 1) * this.riskRegisterPageSize;
        const end = start + this.riskRegisterPageSize;

        return this.filteredRiskRegisterRows.slice(start, end);
    }

    public get riskRegisterVisibleResultsCount(): number {
        return this.filteredRiskRegisterRows.length;
    }

    public get filteredRiskRegisterRows(): RiskRegisterRow[] {
        const normalizedQuery = this.riskRegisterSearchQuery.trim().toLowerCase();

        const rowsAfterSearch = this.riskRegisterRows.filter((row) => {
            if (!normalizedQuery) {
                return true;
            }

            const assetTagMatch = row.assetTag.toLowerCase().includes(normalizedQuery);
            const assetDescriptionMatch = row.assetDescription.toLowerCase().includes(normalizedQuery);

            return assetTagMatch || assetDescriptionMatch;
        });

        const rowsAfterFilters = rowsAfterSearch.filter((row) => {
            const criticalityMatch = this.isAllCriticalitySelected || this.selectedCriticalityValues.includes(row.criticality);
            const badActorMatch = this.isAllPotentialBadActorSelected || this.selectedPotentialBadActorValues.includes(row.potentialBadActor);
            const riskTrendMatch = this.isAllRiskTrendSelected || this.selectedRiskTrendValues.includes(row.risk_trend);
            const woStatusMatch = this.isAllWoStatusSelected || this.selectedWoStatusValues.includes(row.woStatus);
            const riskTypeMatch = this.isAllRiskTypeSelected || this.selectedRiskTypeValues.includes(row.riskType);
            const hasActiveDataSourceFilter = !this.isAllDataSourceSelected && this.selectedDataSourceValues.length > 0;
            const dataSourceMatch = !hasActiveDataSourceFilter || this.selectedDataSourceValues.some((sourceKey) => {
                const sourceValue = this.getSourceFilterValue(row, sourceKey);
                return sourceValue >= 1;
            });

            return criticalityMatch && badActorMatch && riskTrendMatch && woStatusMatch && riskTypeMatch && dataSourceMatch;
        });

        return this.sortRiskRegisterRows(rowsAfterFilters);
    }

    public onRiskRegisterSortChange(key: RiskRegisterSortKey, direction: RiskRegisterSortDirection): void {
        const isAlreadyActive = this.riskRegisterSortKey === key && this.riskRegisterSortDirection === direction;

        this.riskRegisterSortKey = isAlreadyActive ? null : key;
        this.riskRegisterSortDirection = isAlreadyActive ? null : direction;
        this.currentRiskRegisterPage = 1;
    }

    public isRiskRegisterSortActive(key: RiskRegisterSortKey, direction: RiskRegisterSortDirection): boolean {
        return this.riskRegisterSortKey === key && this.riskRegisterSortDirection === direction;
    }

    private sortRiskRegisterRows(rows: RiskRegisterRow[]): RiskRegisterRow[] {
        const sortKey = this.riskRegisterSortKey;
        const direction = this.riskRegisterSortDirection;

        if (!sortKey || !direction) {
            return rows;
        }

        const directionFactor = direction === 'asc' ? 1 : -1;

        return [...rows].sort((leftRow, rightRow) => {
            const leftValue = this.toSortableNumber(leftRow[sortKey]);
            const rightValue = this.toSortableNumber(rightRow[sortKey]);

            // Rows without a numeric value stay at the bottom in both directions.
            if (leftValue === null || rightValue === null) {
                return leftValue === rightValue ? 0 : (leftValue === null ? 1 : -1);
            }

            return (leftValue - rightValue) * directionFactor;
        });
    }

    private toSortableNumber(value: string): number | null {
        const parsedValue = Number.parseFloat(value);

        return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    public get shouldShowRiskRegisterPagination(): boolean {
        return this.filteredRiskRegisterRows.length > this.riskRegisterPageSize;
    }

    public get riskRegisterEmptyStateMessage(): string {
        if (!this.selectedRun) {
            return 'No data available. Select a run to view ranked risk register data.';
        }

        if (this.isRunAssetsLoading) {
            return 'Loading ranked risk register data...';
        }

        if (this.hasRunAssetsLoadError) {
            return 'Unable to load ranked risk register data. Please try again.';
        }

        return 'No data available for the selected run.';
    }

    public get hasAppliedFilters(): boolean {
        return !this.isAllCriticalitySelected
            || !this.isAllPotentialBadActorSelected
            || !this.isAllRiskTrendSelected
            || !this.isAllWoStatusSelected
            || !this.isAllRiskTypeSelected
            || !this.isAllDataSourceSelected;
    }

    public getRankBadgeClass(band: string): string {
        const bandClass = this.getBandClass(band);
        if (!bandClass) {
            return 'rank-default';
        }
        return bandClass;
    }

    public getBandClass(band: string): string {
        const bandUpper = band?.toUpperCase().trim();
        if (bandUpper === 'RED') {
            return 'band-red';
        }
        if (bandUpper === 'AMBER') {
            return 'band-amber';
        }
        if (bandUpper === 'GREEN') {
            return 'band-green';
        }
        return '';
    }

    public getScorePercentage(score: string): number {
        const scoreNum = parseFloat(score);
        if (isNaN(scoreNum)) {
            return 0;
        }
        return Math.min(100, Math.max(0, scoreNum));
    }

    public getScoreFactorsTooltipLabel(row: RiskRegisterRow): string {
        const factors = row.scoreFactors ?? [];

        if (!factors.length) {
            return 'No score factors available';
        }

        return factors.map((factor) => `${factor.key}: ${factor.value}`).join('\n');
    }

    public getCellClass(columnKey: string): string {
        if (columnKey === 'rank') {
            return 'rank-column';
        }
        if (columnKey === 'score') {
            return 'score-column';
        }
        return '';
    }

    public onRiskRegisterSearchChange(value: string): void {
        this.riskRegisterSearchQuery = value;
        this.currentRiskRegisterPage = 1;
    }

    public onMetricCardClick(card: HomeMetricCard): void {
        if (!card.filterKey) {
            return;
        }

        this.applySummaryMetricFilter(card.filterKey);
    }

    public isMetricCardClickable(card: HomeMetricCard): boolean {
        return Boolean(card.filterKey);
    }

    public hasMetricCardTooltip(card: HomeMetricCard): boolean {
        return Boolean(card.tooltip?.trim());
    }

    public isMetricCardActive(card: HomeMetricCard): boolean {
        return Boolean(card.filterKey) && this.activeSummaryMetricFilter === card.filterKey;
    }

    public onMetricCardKeydown(event: KeyboardEvent, card: HomeMetricCard): void {
        if (!this.isMetricCardClickable(card)) {
            return;
        }

        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        this.onMetricCardClick(card);
    }

    public get criticalityFilterOptions(): string[] {
        return this.getDistinctValues('criticality');
    }

    public get potentialBadActorFilterOptions(): string[] {
        return this.getDistinctValues('potentialBadActor');
    }

    public get riskTrendFilterOptions(): string[] {
        return this.getDistinctValues('risk_trend');
    }

    public get woStatusFilterOptions(): string[] {
        return this.getDistinctValues('woStatus');
    }

    public get riskTypeFilterOptions(): string[] {
        return this.getDistinctValues('riskType');
    }

    public get dataSourceFilterOptionLabels(): string[] {
        return this.dataSourceFilterOptions.map((option) => option.label);
    }

    public get activeFilterOptions(): string[] {
        if (this.activeFilterField === 'criticality') {
            return this.criticalityFilterOptions;
        }

        if (this.activeFilterField === 'potentialBadActor') {
            return this.potentialBadActorFilterOptions;
        }

        if (this.activeFilterField === 'risk_trend') {
            return this.riskTrendFilterOptions;
        }

        if (this.activeFilterField === 'woStatus') {
            return this.woStatusFilterOptions;
        }

        if (this.activeFilterField === 'dataSource') {
            return this.dataSourceFilterOptionLabels;
        }

        return this.riskTypeFilterOptions;
    }

    public setActiveFilterField(field: FilterFieldKey): void {
        this.activeFilterField = field;
    }

    public isDialogAllSelected(field: FilterFieldKey): boolean {
        if (field === 'criticality') {
            return this.dialogIsAllCriticalitySelected;
        }

        if (field === 'potentialBadActor') {
            return this.dialogIsAllPotentialBadActorSelected;
        }

        if (field === 'risk_trend') {
            return this.dialogIsAllRiskTrendSelected;
        }

        if (field === 'woStatus') {
            return this.dialogIsAllWoStatusSelected;
        }

        if (field === 'dataSource') {
            return this.dialogIsAllDataSourceSelected;
        }

        return this.dialogIsAllRiskTypeSelected;
    }

    public isDialogOptionSelected(field: FilterFieldKey, option: string): boolean {
        if (this.isDialogAllSelected(field)) {
            return true;
        }

        if (field === 'dataSource') {
            const optionKey = this.getDataSourceFilterOptionKey(option);
            return optionKey ? this.dialogSelectedDataSourceValues[0] === optionKey : false;
        }

        return field === 'criticality'
            ? this.dialogSelectedCriticalityValues.includes(option)
            : field === 'potentialBadActor'
                ? this.dialogSelectedPotentialBadActorValues.includes(option)
                : field === 'risk_trend'
                    ? this.dialogSelectedRiskTrendValues.includes(option)
                    : field === 'woStatus'
                        ? this.dialogSelectedWoStatusValues.includes(option)
                        : this.dialogSelectedRiskTypeValues.includes(option);
    }

    public onAllDialogFilterToggle(field: FilterFieldKey, checked: boolean): void {
        if (field === 'criticality') {
            this.dialogIsAllCriticalitySelected = checked;
            this.dialogSelectedCriticalityValues = [];
        } else if (field === 'potentialBadActor') {
            this.dialogIsAllPotentialBadActorSelected = checked;
            this.dialogSelectedPotentialBadActorValues = [];
        } else if (field === 'risk_trend') {
            this.dialogIsAllRiskTrendSelected = checked;
            this.dialogSelectedRiskTrendValues = [];
        } else if (field === 'woStatus') {
            this.dialogIsAllWoStatusSelected = checked;
            this.dialogSelectedWoStatusValues = [];
        } else if (field === 'dataSource') {
            this.dialogIsAllDataSourceSelected = checked;
            this.dialogSelectedDataSourceValues = [];
        } else {
            this.dialogIsAllRiskTypeSelected = checked;
            this.dialogSelectedRiskTypeValues = [];
        }
    }

    public onDialogFilterOptionToggle(field: FilterFieldKey, option: string, checked: boolean): void {
        if (field === 'dataSource') {
            const optionKey = this.getDataSourceFilterOptionKey(option);

            if (!optionKey) {
                return;
            }

            if (checked) {
                this.dialogIsAllDataSourceSelected = false;
                this.dialogSelectedDataSourceValues = [optionKey];
            } else {
                this.dialogSelectedDataSourceValues = [];
                this.dialogIsAllDataSourceSelected = true;
            }

            return;
        }

        const allOptions = field === 'criticality'
            ? this.criticalityFilterOptions
            : field === 'potentialBadActor'
                ? this.potentialBadActorFilterOptions
                : field === 'risk_trend'
                    ? this.riskTrendFilterOptions
                    : field === 'woStatus'
                        ? this.woStatusFilterOptions
                        : this.riskTypeFilterOptions;

        const optionKey = option;

        // If All is active and the user unchecks one option, switch to explicit selection
        // of all remaining options and uncheck All.
        if (this.isDialogAllSelected(field) && !checked) {
            const nextSelection = allOptions.filter((value) => value !== optionKey);

            if (field === 'criticality') {
                this.dialogIsAllCriticalitySelected = false;
                this.dialogSelectedCriticalityValues = nextSelection;
            } else if (field === 'potentialBadActor') {
                this.dialogIsAllPotentialBadActorSelected = false;
                this.dialogSelectedPotentialBadActorValues = nextSelection;
            } else if (field === 'risk_trend') {
                this.dialogIsAllRiskTrendSelected = false;
                this.dialogSelectedRiskTrendValues = nextSelection;
            } else if (field === 'woStatus') {
                this.dialogIsAllWoStatusSelected = false;
                this.dialogSelectedWoStatusValues = nextSelection;
            } else {
                this.dialogIsAllRiskTypeSelected = false;
                this.dialogSelectedRiskTypeValues = nextSelection;
            }

            return;
        }

        if (field === 'criticality') {
            this.updateOptionSelection(
                allOptions,
                'criticality',
                option,
                checked,
                true
            );
        } else if (field === 'potentialBadActor') {
            this.updateOptionSelection(
                allOptions,
                'potentialBadActor',
                option,
                checked,
                true
            );
        } else if (field === 'risk_trend') {
            this.updateOptionSelection(
                allOptions,
                'risk_trend',
                option,
                checked,
                true
            );
        } else if (field === 'woStatus') {
            this.updateOptionSelection(
                allOptions,
                'woStatus',
                optionKey,
                checked,
                true
            );
        } else {
            this.updateOptionSelection(
                allOptions,
                'riskType',
                optionKey,
                checked,
                true
            );
        }
    }

    public openFilterDialog(): void {
        this.dialogIsAllCriticalitySelected = this.isAllCriticalitySelected;
        this.dialogIsAllPotentialBadActorSelected = this.isAllPotentialBadActorSelected;
        this.dialogIsAllRiskTrendSelected = this.isAllRiskTrendSelected;
        this.dialogIsAllWoStatusSelected = this.isAllWoStatusSelected;
        this.dialogIsAllRiskTypeSelected = this.isAllRiskTypeSelected;
        this.dialogIsAllDataSourceSelected = this.isAllDataSourceSelected;
        this.dialogSelectedCriticalityValues = [...this.selectedCriticalityValues];
        this.dialogSelectedPotentialBadActorValues = [...this.selectedPotentialBadActorValues];
        this.dialogSelectedRiskTrendValues = [...this.selectedRiskTrendValues];
        this.dialogSelectedWoStatusValues = [...this.selectedWoStatusValues];
        this.dialogSelectedRiskTypeValues = [...this.selectedRiskTypeValues];
        this.dialogSelectedDataSourceValues = [...this.selectedDataSourceValues];
        this.isFilterDialogOpen = true;
    }

    public applyDialogFilters(): void {
        this.isAllCriticalitySelected = this.dialogIsAllCriticalitySelected;
        this.isAllPotentialBadActorSelected = this.dialogIsAllPotentialBadActorSelected;
        this.isAllRiskTrendSelected = this.dialogIsAllRiskTrendSelected;
        this.isAllWoStatusSelected = this.dialogIsAllWoStatusSelected;
        this.isAllRiskTypeSelected = this.dialogIsAllRiskTypeSelected;
        this.isAllDataSourceSelected = this.dialogIsAllDataSourceSelected;
        this.selectedCriticalityValues = [...this.dialogSelectedCriticalityValues];
        this.selectedPotentialBadActorValues = [...this.dialogSelectedPotentialBadActorValues];
        this.selectedRiskTrendValues = [...this.dialogSelectedRiskTrendValues];
        this.selectedWoStatusValues = [...this.dialogSelectedWoStatusValues];
        this.selectedRiskTypeValues = [...this.dialogSelectedRiskTypeValues];
        this.selectedDataSourceValues = [...this.dialogSelectedDataSourceValues];
        this.activeSummaryMetricFilter = null;
        this.currentRiskRegisterPage = 1;
        this.closeFilterDialog();
    }

    public clearAppliedFilters(): void {
        this.isAllCriticalitySelected = true;
        this.isAllPotentialBadActorSelected = true;
        this.isAllRiskTrendSelected = true;
        this.isAllWoStatusSelected = true;
        this.isAllRiskTypeSelected = true;
        this.isAllDataSourceSelected = true;
        this.selectedCriticalityValues = [];
        this.selectedPotentialBadActorValues = [];
        this.selectedRiskTrendValues = [];
        this.selectedWoStatusValues = [];
        this.selectedRiskTypeValues = [];
        this.selectedDataSourceValues = [];

        this.dialogIsAllCriticalitySelected = true;
        this.dialogIsAllPotentialBadActorSelected = true;
        this.dialogIsAllRiskTrendSelected = true;
        this.dialogIsAllWoStatusSelected = true;
        this.dialogIsAllRiskTypeSelected = true;
        this.dialogIsAllDataSourceSelected = true;
        this.dialogSelectedCriticalityValues = [];
        this.dialogSelectedPotentialBadActorValues = [];
        this.dialogSelectedRiskTrendValues = [];
        this.dialogSelectedWoStatusValues = [];
        this.dialogSelectedRiskTypeValues = [];
        this.dialogSelectedDataSourceValues = [];

        this.activeSummaryMetricFilter = null;
        this.currentRiskRegisterPage = 1;
    }

    public closeFilterDialog(): void {
        this.isFilterDialogOpen = false;
    }

    public goToPreviousRiskRegisterPage(): void {
        if (this.currentRiskRegisterPage > 1) {
            this.currentRiskRegisterPage -= 1;
        }
    }

    public goToNextRiskRegisterPage(): void {
        if (this.currentRiskRegisterPage < this.totalRiskRegisterPages) {
            this.currentRiskRegisterPage += 1;
        }
    }

    public onRiskRegisterPageSizeChange(pageSize: number): void {
        if (!this.riskRegisterPageSizeOptions.includes(pageSize) || pageSize === this.riskRegisterPageSize) {
            return;
        }

        this.riskRegisterPageSize = pageSize;
        this.currentRiskRegisterPage = 1;
        Variables.set(this.riskRegisterPageSizeStorageKey, pageSize, VariableType.Local);
    }

    private formatReportFileName(fileName: string): string {
        const normalized = fileName
            .replace(/_/g, ' ')
            .replace(/(\D)(\d{4})(\.pdf)$/i, '$1 $2$3')
            .replace(/\s+/g, ' ')
            .trim();

        return normalized;
    }

    private formatMonthYear(isoDate: string): string {
        const date = new Date(isoDate);

        if (Number.isNaN(date.getTime())) {
            return isoDate;
        }

        return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    }

    private getDistinctValues(key: Exclude<FilterFieldKey, 'dataSource'>): string[] {
        return Array.from(new Set(this.riskRegisterRows.map((row) => row[key])));
    }

    private extractRunOptions(response: RunOptionResponse[]): RunOption[] {
        const uniqueRunOptions: RunOption[] = [];
        const seen = new Set<string>();

        for (const item of response || []) {
            const runId = item?.run_id?.trim();
            const runIdMonthYear = item?.run_id_month_year?.trim();

            if (!runId || seen.has(runId)) {
                continue;
            }

            seen.add(runId);
            uniqueRunOptions.push({
                runId,
                label: runIdMonthYear || runId
            });
        }

        return uniqueRunOptions.sort((left, right) => right.runId.localeCompare(left.runId));
    }

    private loadRunSummary(runId: string): void {
        if (!this.homeApiService) {
            return;
        }

        const requestId = ++this.runSummaryRequestId;
        this.isRunSummaryLoading = true;
        this.hasRunSummaryLoadError = false;

        this.homeApiService.getRunSummary<RunSummaryResponse>(runId, this.selectedRefineryId, this.selectedUnitId).subscribe({
            next: (response) => {
                if (requestId !== this.runSummaryRequestId) {
                    return;
                }

                this.applyRunSummary(response);
                this.isRunSummaryLoading = false;
            },
            error: () => {
                if (requestId !== this.runSummaryRequestId) {
                    return;
                }

                this.resetRunSummaryDisplay();
                this.hasRunSummaryLoadError = true;
                this.isRunSummaryLoading = false;
            }
        });
    }

    private loadRunAssets(runId: string): void {
        if (!this.homeApiService) {
            return;
        }

        const requestId = ++this.runAssetsRequestId;
        this.isRunAssetsLoading = true;
        this.hasRunAssetsLoadError = false;

        this.homeApiService.getRunAssets<RunAssetsResponse>(runId, this.selectedRefineryId, this.selectedUnitId).subscribe({
            next: (response) => {
                if (requestId !== this.runAssetsRequestId) {
                    return;
                }

                const mappedRows = (response?.items ?? []).map((item) => this.mapRiskRegisterRow(item));

                this.riskRegisterRows = mappedRows;
                this.riskRegisterTotalResults = response?.total_items ?? mappedRows.length;
                this.currentRiskRegisterPage = 1;
                this.isRunAssetsLoading = false;
            },
            error: () => {
                if (requestId !== this.runAssetsRequestId) {
                    return;
                }

                this.riskRegisterRows = [];
                this.clearRiskDetailState();
                this.riskRegisterTotalResults = 0;
                this.hasRunAssetsLoadError = true;
                this.currentRiskRegisterPage = 1;
                this.isRunAssetsLoading = false;
            }
        });
    }

    private loadMonthlyReport(runId: string): void {
        if (!this.homeApiService) {
            return;
        }

        const requestId = ++this.monthlyReportRequestId;
        this.isMonthlyReportLoading = true;
        this.hasMonthlyReportLoadError = false;

        this.homeApiService.getProcessSummary<MonthlyReportResponse>(runId, this.selectedRefineryId, this.selectedUnitId).subscribe({
            next: (response) => {
                if (requestId !== this.monthlyReportRequestId) {
                    return;
                }

                try {
                    this.applyMonthlyReport(response);
                } catch (err) {
                    console.error('Error applying monthly report', err);
                    this.resetMonthlyReportDisplay();
                    this.hasMonthlyReportLoadError = true;
                }

                this.isMonthlyReportLoading = false;
            },
            error: () => {
                if (requestId !== this.monthlyReportRequestId) {
                    return;
                }

                this.resetMonthlyReportDisplay();
                this.hasMonthlyReportLoadError = true;
                this.isMonthlyReportLoading = false;
            }
        });
    }

    private loadCitationsManifest(runId: string): void {
        if (!this.homeApiService || !runId) {
            return;
        }

        this.isCitationsManifestLoading = true;
        this.hasCitationsManifestLoadError = false;

        this.homeApiService.getCitationsManifest<CitationsManifestResponse>(runId, this.selectedRefineryId, this.selectedUnitId).subscribe({
            next: (response) => {
                this.citationsManifest = response;
                this.isCitationsManifestLoading = false;
            },
            error: () => {
                this.citationsManifest = null;
                this.hasCitationsManifestLoadError = true;
                this.isCitationsManifestLoading = false;
            }
        });
    }

    private applyMonthlyReport(response: MonthlyReportResponse): void {
        const firstReport = response?.reports?.[0] ?? null;

        this.processEngineeringMonthlyReportResponse = firstReport
            ? { run_id: response?.run_id ?? this.selectedRun, reports: [firstReport] }
            : { run_id: response?.run_id ?? this.selectedRun, reports: [] };

        this.monthlyReportIssueRows = (firstReport?.failure_analysis ?? []).map((item) => ({
            type: this.toDisplayValue(item?.issue_type),
            assetUnit: this.toDisplayValue(item?.asset),
            issueDescription: this.toDisplayValue(item?.description),
            detailRootCause: this.toDisplayValue(item?.detail)
        }));

        this.monthlyEquipmentReferencedRows = (firstReport?.asset_mentions ?? []).map((item) => ({
            tag: this.toDisplayValue(item?.tag),
            equipmentName: this.toDisplayValue(item?.description),
            contextFromReport: this.toDisplayValue(item?.context),
            significance: this.toDisplayValue(item?.significance)
        }));

        this.monthlyItemsRequiringAttention = ((firstReport?.items_requiring_attention ?? firstReport?.operational_concerns ?? []) as (MonthlyReportItemRequiringAttention | string)[])
            .map((item): string => {
                if (typeof item === 'string') return item.trim();
                const typedItem = item as MonthlyReportItemRequiringAttention;
                const parts = [typedItem.priority ? `[${typedItem.priority}]` : '', typedItem.asset_system, typedItem.risk_description].filter(Boolean);
                return parts.join(' — ');
            })
            .filter(Boolean);

        this.monthlyOpportunities = ((firstReport?.opportunities ?? []) as (MonthlyReportOpportunity | string)[])
            .map((item): string => {
                if (typeof item === 'string') return item.trim();
                const typedItem = item as MonthlyReportOpportunity;
                return typedItem.expected_benefit ? `${typedItem.opportunity} (${typedItem.expected_benefit})` : typedItem.opportunity;
            })
            .filter(Boolean);
    }

    private applyRunSummary(summary: RunSummaryResponse): void {
        const refinery = summary?.refinery?.trim() ?? '';
        const unit = summary?.unit?.trim() ?? '';
        this.reportTitle = refinery && unit ? `${refinery}-${unit}` : '--';

        const dateFrom = summary?.date_from?.trim() ?? '';
        const dateTo = summary?.date_to?.trim() ?? '';
        const formattedDateFrom = dateFrom ? this.formatDateToReadable(dateFrom) : '';
        const formattedDateTo = dateTo ? this.formatDateToReadable(dateTo) : '';
        this.reportingPeriodValue = formattedDateFrom && formattedDateTo ? `${formattedDateFrom} to ${formattedDateTo}` : '--';

        const assetsWithSignals = summary.assets_analysed ?? summary.assets_analyzed ?? 0;
        this.setMetricCardValue('Asset Tags', assetsWithSignals);
        this.setMetricCardValue('Potential Reliability Risk', summary.bad_actor_candidates ?? 0);
        this.setMetricCardValue('Safety risk type', summary.safety_risk_type_count ?? 0);
        this.setMetricCardValue('Potential LPO risk type', summary.lpo_risk_type_count ?? 0);
        this.setMetricCardValue('Generated', this.formatGeneratedDate(summary.generated_at));
        this.runSummaryRegenerationRequired = Boolean(summary?.regeneration_required);
        this.runSummaryLastUpdatedLabel = this.formatGeneratedTimestamp(summary.generated_at);
    }

    private resetRunSummaryDisplay(): void {
        this.reportTitle = '--';
        this.reportingPeriodValue = '--';
        this.runSummaryRegenerationRequired = false;
        this.runSummaryLastUpdatedLabel = '--';
        this.setMetricCardValue('Asset Tags', '--');
        this.setMetricCardValue('Potential Reliability Risk', '--');
        this.setMetricCardValue('Safety risk type', '--');
        this.setMetricCardValue('Potential LPO risk type', '--');
        this.setMetricCardValue('Generated', '--');
    }

    private resetMonthlyReportDisplay(): void {
        this.processEngineeringMonthlyReportResponse = null;
        this.monthlyReportIssueRows = [];
        this.monthlyEquipmentReferencedRows = [];
        this.monthlyItemsRequiringAttention = [];
        this.monthlyOpportunities = [];
    }

    private resetCitationsManifestState(): void {
        this.citationsManifest = null;
        this.isCitationsManifestLoading = false;
        this.hasCitationsManifestLoadError = false;
    }

    private resetRiskRegisterDisplay(): void {
        this.riskRegisterRows = [];
        this.hasRunAssetsLoadError = false;
        this.isRunAssetsLoading = false;
    }

    private formatGeneratedDate(generatedAt?: string): string {
        const rawValue = generatedAt?.trim();
        if (!rawValue) {
            return '--';
        }

        return this.formatDateToReadable(rawValue);
    }

    private formatGeneratedTimestamp(generatedAt?: string): string {
        const rawValue = generatedAt?.trim();
        if (!rawValue) {
            return '--';
        }

        const parsed = new Date(rawValue);
        if (Number.isNaN(parsed.getTime())) {
            return rawValue;
        }

        const datePart = formatMDY(rawValue);
        const hours = parsed.getUTCHours().toString().padStart(2, '0');
        const minutes = parsed.getUTCMinutes().toString().padStart(2, '0');

        return `${datePart}, ${hours}:${minutes} UTC`;
    }

    private formatDateToReadable(dateStr: string): string {
        const formatted = formatMDY(dateStr);
        return formatted || dateStr;
    }

    private formatDateOnly(value?: string | null): string {
        const rawValue = value?.trim() ?? '';
        if (!rawValue) {
            return '--';
        }

        if (rawValue.toLowerCase() === 'unknown_date') {
            return '--';
        }

        const formatted = formatMDY(rawValue);
        if (!formatted) {
            return this.toDisplayValue(rawValue);
        }

        return formatted;
    }

    private normalizeIssueType(type: string): string {
        return type.trim().toLowerCase();
    }

    private getRefineryById(refineryId: string): RefineryLandingCard | null {
        if (!refineryId) {
            return null;
        }

        return this.refineryLandingCards.find((card) => card.id === refineryId) ?? null;
    }

    private findRefineryUnitByRunId(runId: string): { refineryId: string; unitId: string } | null {
        const normalizedRunId = runId.trim();

        if (!normalizedRunId) {
            return null;
        }

        for (const refinery of this.refineryLandingCards) {
            for (const unit of refinery.units) {
                if (unit.runs.some((run) => run.runId === normalizedRunId)) {
                    return { refineryId: refinery.id, unitId: unit.id };
                }
            }
        }

        return null;
    }

    private setMetricCardValue(title: string, value: string | number): void {
        const targetCard = this.metricCards.find((card) => card.title === title);

        if (!targetCard) {
            return;
        }

        targetCard.value = value.toString();
    }

    private mapRiskRegisterRow(asset: RunAssetsApiRow): RiskRegisterRow {
        const priorityNumber = asset?.priority_number;
        const priorityLabel = asset?.priority_label?.trim();
        const normalizedRiskType = this.normalizeRiskTypeDisplay(asset?.risk_type);

        const row: RiskRegisterRow = {
            rank: this.toDisplayValue(asset?.risk_rank),
            assetTag: this.toDisplayValue(asset?.asset_tag),
            assetDescription: this.toDisplayValue(asset?.location_description),
            woStatus: this.toDisplayValue(asset?.wo_status_label),
            priority: priorityNumber !== null && priorityNumber !== undefined
                ? priorityNumber.toString()
                : this.toDisplayValue(priorityLabel),
            riskType: this.toDisplayValue(normalizedRiskType),
            criticality: this.toDisplayValue(asset?.criticality),
            failureType: this.toDisplayValue(asset?.failure_type),
            rsl: this.toDisplayValue(asset?.rsl_count),
            stl: this.toDisplayValue(asset?.stl_count),
            iow: this.toDisplayValue(asset?.iow_count),
            ord: this.toDisplayValue(asset?.ord_count),
            predix: this.toDisplayValue(asset?.predix_count),
            maximoCmWos: this.toDisplayValue(asset?.maximo_count),
            score: this.toDisplayValue(asset?.score_100),
            scoreFactors: this.getScoreFactors(asset?.score_factors),
            potentialBadActor: asset?.is_bad_actor ? 'Yes' : 'No',
            severity: this.formatDecimal(asset?.severity, 2),
            band: this.toDisplayValue(asset?.band),
            aiAdjustedRiskScore: this.formatDecimal(asset?.ai_adjusted_risk_score, 2),
            aiAdjustedRiskCalculation: this.toBulletPoints(asset?.ai_adjusted_risk_calculation ?? undefined),
            semanticRiskScore: this.formatDecimal(asset?.semantic_risk_score, 2),
            semanticSupportingEvidence: this.toSemanticSupportingEvidence(asset?.semantic_supporting_evidence),
            semanticRationale: (asset?.rationale ?? asset?.semantic_risk_reason ?? '').trim(),
            explTrend: this.formatDecimal(asset?.trend_worsening, 2),
            risk_trend: this.toDisplayValue(asset?.risk_trend),
            isDeleted: Boolean(asset?.is_deleted),
            deletedAt: asset?.deleted_at ?? null,
            deletedByName: asset?.deleted_by_name ?? null,
            deletedComment: asset?.deleted_comment ?? null,
            isExpanded: false
        };

        const storedEdit = this.getStoredRiskEdits()[`${row.rank}-${row.assetTag}`];

        if (storedEdit) {
            row.riskType = storedEdit.riskType ?? row.riskType;
            row.failureType = storedEdit.failureType ?? row.failureType;
        }

        return row;
    }

    private getScoreFactors(scoreFactors?: Record<string, number>): Array<{ key: string; value: string }> {
        if (!scoreFactors || typeof scoreFactors !== 'object') {
            return [];
        }

        return Object.entries(scoreFactors)
            .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
            .map(([key, value]) => ({
                key,
                value: Number.isFinite(value) ? value.toFixed(4).replace(/0+$/, '').replace(/\.$/, '') : '--'
            }));
    }

    private toSemanticSupportingEvidence(evidence?: unknown[]): string[] {
        if (!Array.isArray(evidence) || evidence.length === 0) {
            return [];
        }

        return evidence
            .map((entry) => {
                if (entry === null || entry === undefined) {
                    return '';
                }

                if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
                    return entry.toString().trim();
                }

                try {
                    return JSON.stringify(entry);
                } catch {
                    return '';
                }
            })
            .filter((entry) => Boolean(entry));
    }

    private normalizeRiskTypeDisplay(value: string | null | undefined): string | null | undefined {
        const trimmed = value?.trim();

        if (!trimmed) {
            return value;
        }

        if (trimmed.toLowerCase() === 'not found') {
            return '--';
        }

        return value;
    }

    private loadRiskAssetDetail(row: RiskRegisterRow, onLoaded?: () => void): void {
        const rowKey = this.getRiskDetailRowKey(row);

        if (this.riskDetailResponseByRow[rowKey]) {
            onLoaded?.();
            return;
        }

        if (onLoaded) {
            if (!this.riskDetailLoadCallbacks[rowKey]) {
                this.riskDetailLoadCallbacks[rowKey] = [];
            }
            this.riskDetailLoadCallbacks[rowKey].push(onLoaded);
        }

        if (!this.homeApiService || !this.selectedRun || !this.selectedRefineryId || !this.selectedUnitId || this.riskDetailLoadingByRow[rowKey]) {
            if (!this.selectedRefineryId || !this.selectedUnitId) {
                this.riskDetailLoadErrorByRow[rowKey] = true;
            }
            return;
        }

        this.riskDetailLoadingByRow[rowKey] = true;
        this.riskDetailLoadErrorByRow[rowKey] = false;

        this.homeApiService.getAssetDetail<AssetDetailResponse>(this.selectedRun, this.selectedRefineryId, this.selectedUnitId, row.assetTag).subscribe({
            next: (response) => {
                this.riskDetailResponseByRow[rowKey] = response;
                this.riskDetailLoadingByRow[rowKey] = false;
                this.flushRiskDetailLoadCallbacks(rowKey);
            },
            error: () => {
                this.riskDetailLoadErrorByRow[rowKey] = true;
                this.riskDetailLoadingByRow[rowKey] = false;
            }
        });
    }

    private flushRiskDetailLoadCallbacks(rowKey: string): void {
        const callbacks = this.riskDetailLoadCallbacks[rowKey];

        if (!callbacks) {
            return;
        }

        delete this.riskDetailLoadCallbacks[rowKey];
        callbacks.forEach((callback) => callback());
    }

    private populateEditDialogForm(row: RiskRegisterRow): void {
        const storedEdit = this.getStoredRiskEditForRow(row);

        this.editDialogForm = {
            mainConcern: this.getRiskDetailMainConcern(row),
            recommendedAction: this.getRiskDetailRecommendedAction(row),
            comment: storedEdit?.comment ?? '',
            riskType: row.riskType,
            failureType: row.failureType
        };
    }

    private getStoredRiskEdits(): Record<string, RiskEditForm> {
        if (typeof sessionStorage === 'undefined') {
            return {};
        }

        try {
            const raw = sessionStorage.getItem(this.riskEditsStorageKey);
            return raw ? (JSON.parse(raw) as Record<string, RiskEditForm>) : {};
        } catch {
            return {};
        }
    }

    private setStoredRiskEdits(edits: Record<string, RiskEditForm>): void {
        if (typeof sessionStorage === 'undefined') {
            return;
        }

        sessionStorage.setItem(this.riskEditsStorageKey, JSON.stringify(edits));
    }

    private getStoredRiskEditForRow(row: RiskRegisterRow): RiskEditForm | undefined {
        return this.getStoredRiskEdits()[this.getRiskDetailRowKey(row)];
    }

    private loadRiskSourceMentions(row: RiskRegisterRow): void {
        if (!this.homeApiService || !this.selectedRun || !this.selectedRefineryId || !this.selectedUnitId) {
            return;
        }

        const rowKey = this.getRiskDetailRowKey(row);

        if (this.riskDetailSourceMentionsLoadingByRow[rowKey] || this.riskDetailSourceMentionsByRow[rowKey]) {
            return;
        }

        this.riskDetailSourceMentionsLoadingByRow[rowKey] = true;
        this.riskDetailSourceMentionsLoadErrorByRow[rowKey] = false;

        this.homeApiService
            .getAssetMentions<AssetSourceMentionsResponse>(this.selectedRun, this.selectedRefineryId, this.selectedUnitId, row.assetTag, 1, 100)
            .subscribe({
            next: (response) => {
                this.riskDetailSourceMentionsByRow[rowKey] = (response?.items ?? []).map((item) => ({
                    source: this.toDisplayValue(item?.source),
                    date: this.formatDateOnly(item?.date_occurred),
                    description: this.toDisplayValue(item?.description),
                    repetitionTimes: this.toDisplayValue(item?.repetition_times),
                    woStatus: this.toDisplayValue(item?.wo_status),
                    ref: this.toDisplayValue(item?.record_ref)
                }));
                this.riskDetailSourceMentionsTotalByRow[rowKey] = response?.total_items ?? this.riskDetailSourceMentionsByRow[rowKey].length;
                this.riskDetailSourceMentionsLoadingByRow[rowKey] = false;
            },
            error: () => {
                this.riskDetailSourceMentionsLoadErrorByRow[rowKey] = true;
                this.riskDetailSourceMentionsLoadingByRow[rowKey] = false;
            }
            });
    }

    private getRiskDetailResponse(row: RiskRegisterRow): AssetDetailResponse | null {
        return this.riskDetailResponseByRow[this.getRiskDetailRowKey(row)] ?? null;
    }

    private toBulletPoints(rawText?: string): string[] {
        const normalized = (rawText ?? '').trim();

        if (!normalized) {
            return [];
        }

        const points = normalized
            .split(/\r?\n+/)
            .map((line) => line.replace(/^\s*[•\-*]\s*/, '').trim())
            .filter((line) => Boolean(line));

        return points.length > 0 ? points : [normalized];
    }

    private toPmMention(mention: string | Record<string, unknown>): string {
        if (typeof mention === 'string') {
            return mention.trim();
        }

        const values = Object.values(mention ?? {})
            .map((value) => (value === null || value === undefined ? '' : value.toString().trim()))
            .filter((value) => Boolean(value));

        return values.join(' | ');
    }

    private parseTrendPoint(rawPoint: string): RiskDetailTrendPoint {
        const point = rawPoint.trim();
        const dateWithContentMatch = point.match(/^((?:19|20)\d{2}[-/](?:0[1-9]|1[0-2])(?:[-/](?:0[1-9]|[12]\d|3[01]))?)\s*[:\-]\s*(.+)$/);

        if (dateWithContentMatch) {
            return {
                date: formatMDY(dateWithContentMatch[1]) || dateWithContentMatch[1],
                content: dateWithContentMatch[2].trim()
            };
        }

        const genericSplitIndex = point.indexOf(':');

        if (genericSplitIndex > 0) {
            const rawDate = point.slice(0, genericSplitIndex).trim();
            return {
                date: formatMDY(rawDate) || rawDate,
                content: point.slice(genericSplitIndex + 1).trim()
            };
        }

        return {
            date: 'Date unavailable',
            content: point
        };
    }

    private getFirstTrendPointPerMonth(points: RiskDetailTrendPoint[], allowedMonthKeys: Set<string> | null = null): RiskDetailTrendPoint[] {
        const sortedPoints = [...points].sort((left, right) => {
            const leftEpoch = this.toTrendPointEpoch(left.date);
            const rightEpoch = this.toTrendPointEpoch(right.date);

            if (leftEpoch !== null && rightEpoch !== null) {
                return leftEpoch - rightEpoch;
            }

            if (leftEpoch !== null) {
                return -1;
            }

            if (rightEpoch !== null) {
                return 1;
            }

            return 0;
        });

        const seenMonths = new Set<string>();

        return sortedPoints.filter((point) => {
            const monthKey = this.toTrendMonthKey(point.date);

            if (allowedMonthKeys) {
                if (!monthKey || !allowedMonthKeys.has(monthKey)) {
                    return false;
                }
            }

            if (!monthKey) {
                return true;
            }

            if (seenMonths.has(monthKey)) {
                return false;
            }

            seenMonths.add(monthKey);
            return true;
        });
    }

    private getAllowedTrendMonthKeysForSelectedRun(): Set<string> | null {
        const match = this.selectedRun.trim().match(/^((?:19|20)\d{2})-(0[1-9]|1[0-2])$/);

        if (!match) {
            return null;
        }

        const year = Number(match[1]);
        const month = Number(match[2]);
        const months = new Set<string>();

        for (let offset = 5; offset >= 0; offset -= 1) {
            const date = new Date(Date.UTC(year, month - 1 - offset, 1));
            const monthLabel = String(date.getUTCMonth() + 1).padStart(2, '0');

            months.add(`${date.getUTCFullYear()}-${monthLabel}`);
        }

        return months;
    }

    private toTrendMonthKey(dateLabel: string): string | null {
        const normalized = (dateLabel ?? '').trim();

        if (!normalized || normalized.toLowerCase() === 'date unavailable') {
            return null;
        }

        const yearFirstMatch = normalized.match(/^((?:19|20)\d{2})[-/](0[1-9]|1[0-2])(?:[-/](0[1-9]|[12]\d|3[01]))?$/);
        if (yearFirstMatch) {
            return `${yearFirstMatch[1]}-${yearFirstMatch[2]}`;
        }

        const monthFirstMatch = normalized.match(/^(0[1-9]|1[0-2])[-/](?:(0[1-9]|[12]\d|3[01])[-/])?((?:19|20)\d{2})$/);
        if (monthFirstMatch) {
            return `${monthFirstMatch[3]}-${monthFirstMatch[1]}`;
        }

        return null;
    }

    private toTrendPointEpoch(dateLabel: string): number | null {
        const normalized = (dateLabel ?? '').trim();

        if (!normalized || normalized.toLowerCase() === 'date unavailable') {
            return null;
        }

        const yearFirstMatch = normalized.match(/^((?:19|20)\d{2})[-/](0[1-9]|1[0-2])(?:[-/](0[1-9]|[12]\d|3[01]))?$/);
        if (yearFirstMatch) {
            const year = Number(yearFirstMatch[1]);
            const month = Number(yearFirstMatch[2]);
            const day = Number(yearFirstMatch[3] ?? '1');
            const epoch = Date.UTC(year, month - 1, day);
            return Number.isNaN(epoch) ? null : epoch;
        }

        const monthFirstMatch = normalized.match(/^(0[1-9]|1[0-2])[-/](?:(0[1-9]|[12]\d|3[01])[-/])?((?:19|20)\d{2})$/);
        if (monthFirstMatch) {
            const month = Number(monthFirstMatch[1]);
            const day = Number(monthFirstMatch[2] ?? '1');
            const year = Number(monthFirstMatch[3]);
            const epoch = Date.UTC(year, month - 1, day);
            return Number.isNaN(epoch) ? null : epoch;
        }

        return null;
    }

    private formatWeight(weight: number | null | undefined): string {
        if (weight === null || weight === undefined || Number.isNaN(weight)) {
            return '--';
        }

        return weight.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    }

    private formatDecimal(value: number | null | undefined, digits: number): string {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return '--';
        }

        return value.toFixed(digits);
    }

    private toDisplayValue(value: string | number | null | undefined): string {
        if (value === null || value === undefined) {
            return '--';
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed.toLowerCase() === 'not found') {
                return '--';
            }
            return trimmed || '--';
        }

        return value.toString();
    }

    private getSortedApprovalHistoryEntries(entries?: AssetDetailApprovalHistoryEntry[]): AssetDetailApprovalHistoryEntry[] {
        if (!Array.isArray(entries) || entries.length === 0) {
            return [];
        }

        return [...entries].sort((left, right) => this.toEpoch(left.modified_at) - this.toEpoch(right.modified_at));
    }

    private toEpoch(value?: string | null): number {
        if (!value) {
            return 0;
        }

        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }

    private formatApprovalHistoryValue(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '--';
        }

        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return '--';
            }
        }

        return String(value);
    }

    private updateOptionSelection(
        allOptions: string[],
        field: FilterFieldKey,
        option: string,
        checked: boolean,
        useDialogState: boolean
    ): void {
        const currentSelection = field === 'criticality'
            ? useDialogState
                ? [...this.dialogSelectedCriticalityValues]
                : [...this.selectedCriticalityValues]
            : field === 'potentialBadActor'
                ? useDialogState
                    ? [...this.dialogSelectedPotentialBadActorValues]
                    : [...this.selectedPotentialBadActorValues]
                : field === 'risk_trend'
                    ? useDialogState
                        ? [...this.dialogSelectedRiskTrendValues]
                        : [...this.selectedRiskTrendValues]
                    : field === 'woStatus'
                        ? useDialogState
                            ? [...this.dialogSelectedWoStatusValues]
                            : [...this.selectedWoStatusValues]
                        : field === 'dataSource'
                            ? useDialogState
                                ? [...this.dialogSelectedDataSourceValues]
                                : [...this.selectedDataSourceValues]
                        : useDialogState
                            ? [...this.dialogSelectedRiskTypeValues]
                            : [...this.selectedRiskTypeValues];

        const dedupedSelection = checked
            ? Array.from(new Set([...currentSelection, option]))
            : currentSelection.filter((value) => value !== option);

        const isEverythingSelected = dedupedSelection.length === allOptions.length;

        if (field === 'criticality') {
            if (useDialogState) {
                this.dialogIsAllCriticalitySelected = isEverythingSelected;
                this.dialogSelectedCriticalityValues = this.dialogIsAllCriticalitySelected ? [] : dedupedSelection;
            } else {
                this.isAllCriticalitySelected = isEverythingSelected;
                this.selectedCriticalityValues = this.isAllCriticalitySelected ? [] : dedupedSelection;
            }
        } else if (field === 'potentialBadActor') {
            if (useDialogState) {
                this.dialogIsAllPotentialBadActorSelected = isEverythingSelected;
                this.dialogSelectedPotentialBadActorValues = this.dialogIsAllPotentialBadActorSelected ? [] : dedupedSelection;
            } else {
                this.isAllPotentialBadActorSelected = isEverythingSelected;
                this.selectedPotentialBadActorValues = this.isAllPotentialBadActorSelected ? [] : dedupedSelection;
            }
        } else if (field === 'risk_trend') {
            if (useDialogState) {
                this.dialogIsAllRiskTrendSelected = isEverythingSelected;
                this.dialogSelectedRiskTrendValues = this.dialogIsAllRiskTrendSelected ? [] : dedupedSelection;
            } else {
                this.isAllRiskTrendSelected = isEverythingSelected;
                this.selectedRiskTrendValues = this.isAllRiskTrendSelected ? [] : dedupedSelection;
            }
        } else if (field === 'woStatus') {
            if (useDialogState) {
                this.dialogIsAllWoStatusSelected = isEverythingSelected;
                this.dialogSelectedWoStatusValues = this.dialogIsAllWoStatusSelected ? [] : dedupedSelection;
            } else {
                this.isAllWoStatusSelected = isEverythingSelected;
                this.selectedWoStatusValues = this.isAllWoStatusSelected ? [] : dedupedSelection;
            }
        } else if (field === 'dataSource') {
            if (useDialogState) {
                this.dialogIsAllDataSourceSelected = isEverythingSelected;
                this.dialogSelectedDataSourceValues = this.dialogIsAllDataSourceSelected ? [] : dedupedSelection;
            } else {
                this.isAllDataSourceSelected = isEverythingSelected;
                this.selectedDataSourceValues = this.isAllDataSourceSelected ? [] : dedupedSelection;
            }
        } else {
            if (useDialogState) {
                this.dialogIsAllRiskTypeSelected = isEverythingSelected;
                this.dialogSelectedRiskTypeValues = this.dialogIsAllRiskTypeSelected ? [] : dedupedSelection;
            } else {
                this.isAllRiskTypeSelected = isEverythingSelected;
                this.selectedRiskTypeValues = this.isAllRiskTypeSelected ? [] : dedupedSelection;
            }
        }
    }

    private getDataSourceFilterOptionKey(optionLabel: string): string | undefined {
        return this.dataSourceFilterOptions.find((option) => option.label === optionLabel)?.key;
    }

    private getSourceFilterValue(row: RiskRegisterRow, sourceKey: string): number {
        if (!Object.prototype.hasOwnProperty.call(row, sourceKey)) {
            return 0;
        }

        const value = row[sourceKey as keyof RiskRegisterRow];
        const parsed = Number.parseFloat(String(value ?? '').trim());

        return Number.isFinite(parsed) ? parsed : 0;
    }

    private applySummaryMetricFilter(filterKey: SummaryMetricFilterKey): void {
        this.clearAppliedFilters();

        if (filterKey === 'allRows') {
            this.activeSummaryMetricFilter = filterKey;
            this.currentRiskRegisterPage = 1;
            return;
        }

        if (filterKey === 'potentialReliabilityRisk') {
            this.setSingleFilterValue('potentialBadActor', 'Yes');
        } else if (filterKey === 'safetyRiskType') {
            this.setSingleFilterValue('riskType', 'Safety');
        } else if (filterKey === 'potentialLpoRiskType') {
            this.setSingleFilterValue('riskType', 'Potential LPO');
        }

        this.activeSummaryMetricFilter = filterKey;
        this.currentRiskRegisterPage = 1;
    }

    private setSingleFilterValue(field: FilterFieldKey, value: string): void {
        if (field === 'criticality') {
            this.isAllCriticalitySelected = false;
            this.selectedCriticalityValues = [value];
            return;
        }

        if (field === 'potentialBadActor') {
            this.isAllPotentialBadActorSelected = false;
            this.selectedPotentialBadActorValues = [this.resolveFilterValue(this.potentialBadActorFilterOptions, value)];
            return;
        }

        if (field === 'risk_trend') {
            this.isAllRiskTrendSelected = false;
            this.selectedRiskTrendValues = [value];
            return;
        }

        if (field === 'woStatus') {
            this.isAllWoStatusSelected = false;
            this.selectedWoStatusValues = [value];
            return;
        }

        this.isAllRiskTypeSelected = false;
        this.selectedRiskTypeValues = [this.resolveFilterValue(this.riskTypeFilterOptions, value)];
    }

    private resolveFilterValue(options: string[], requestedValue: string): string {
        const normalizedRequestedValue = requestedValue.trim().toLowerCase();
        const matchedOption = options.find((option) => option.trim().toLowerCase() === normalizedRequestedValue);

        return matchedOption ?? requestedValue;
    }

    private ensureRiskDetailStateForRow(row: RiskRegisterRow, forceReset = false): void {
        const key = this.getRiskDetailRowKey(row);

        if (this.riskDetailExpandedByRow[key] && !forceReset) {
            return;
        }

        this.riskDetailExpandedByRow[key] = { ...this.riskDetailSectionDefaults };
    }

    private getRiskDetailRowKey(row: RiskRegisterRow): string {
        return `${row.rank}-${row.assetTag}`;
    }

    private clearRiskDetailState(): void {
        for (const key of Object.keys(this.riskDetailExpandedByRow)) {
            delete this.riskDetailExpandedByRow[key];
        }

        for (const key of Object.keys(this.riskDetailResponseByRow)) {
            delete this.riskDetailResponseByRow[key];
        }

        for (const key of Object.keys(this.riskDetailLoadingByRow)) {
            delete this.riskDetailLoadingByRow[key];
        }

        for (const key of Object.keys(this.riskDetailLoadErrorByRow)) {
            delete this.riskDetailLoadErrorByRow[key];
        }

        for (const key of Object.keys(this.riskDetailSourceMentionsByRow)) {
            delete this.riskDetailSourceMentionsByRow[key];
        }

        for (const key of Object.keys(this.riskDetailSourceMentionsLoadingByRow)) {
            delete this.riskDetailSourceMentionsLoadingByRow[key];
        }

        for (const key of Object.keys(this.riskDetailSourceMentionsLoadErrorByRow)) {
            delete this.riskDetailSourceMentionsLoadErrorByRow[key];
        }

        for (const key of Object.keys(this.riskDetailSourceMentionsTotalByRow)) {
            delete this.riskDetailSourceMentionsTotalByRow[key];
        }
    }
}
